import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  DashboardArchiveEntry,
  DashboardArchiveSetInput,
  DashboardFavoriteSetInput,
  DashboardPreferences,
  GameSummary,
  GameSummaryLite,
  GameStatus,
  GameStatusBadge,
  PublishProgress,
  DiscoveredGame,
  ImportGamePreview,
} from '@volley/crucible-ipc-types/ipc-channels';
import { useIpcQuery } from '../hooks/useIpc';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useCardVisibility } from '../hooks/useCardVisibility';
import { useGameStatusLite } from '../hooks/useGameStatusLite';
import { useTemplateUpdateCheck } from '../hooks/useTemplateUpdateCheck';
import { useClientCapabilities } from '../transport/context';
import { canInvokeChannel } from '@volley/crucible-client';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { OnboardingOverlay } from '../components/OnboardingOverlay';
import { GameStatusPanel, formatTimeAgo } from '../components/GameStatusPanel';
import { TemplateUpdateBadge } from '../components/TemplateUpdateBadge';
import { InterviewInProgressBadge } from '../components/InterviewInProgressBadge';
import { InstallStateChip } from '../components/InstallStateChip';
import { UpdateTemplateModal } from '../components/UpdateTemplateModal';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { SwitchToCloudFoundryButton } from '../components/AppModeSwitch';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { useAppFrame } from '../AppFrameContext';
import {
  CreateGameInterviewDepthToggle,
  type CreateGameKickoffMode,
} from '../components/CreateGameInterviewDepthToggle';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { buildPrereqError } from './prereq-error';
import { slugifyToKebab } from './slugify';
import { deriveCreateNameFromConcept } from './create-name';
import { buildCreateHandoffRouteState, type CreateHandoffRouteState } from './create-handoff';
import {
  deriveCreateDescription,
  GAME_DESCRIPTION_MAX_LENGTH,
  normalizeCreateDescription,
} from './create-description';
import { GenreFallback } from '../components/GenreFallback';
import { UpdateBanner } from '../components/UpdateBanner';
import {
  Settings as SettingsIcon,
  Download,
  Check,
  RefreshCw,
  AlertTriangle,
  Plus,
  Search,
  X,
  Archive,
  ExternalLink,
  RotateCcw,
  Sparkles,
  Star,
  Flame,
  Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { inferGenreKey } from '@volley/crucible-ipc-types/genre-fallback';
import {
  countHiddenTestGames,
  dashboardPreferenceKey,
  filterDashboardCommunityGames,
  filterDashboardLocalGames,
  isDashboardFavorited,
  resolveDashboardFavorites,
  type DashboardFavoriteCard,
  type LocalGamePreferenceSummary,
} from './dashboard-preferences';

function useGitHubUser() {
  return useIpcQuery('github:get-user', undefined, { staleTime: 5 * 60_000 });
}

interface DashboardLocalPreferenceGame extends LocalGamePreferenceSummary {
  displayName: string;
  repoUrl?: string;
}

const DASHBOARD_NEW_UI_SETTINGS_BUTTON_CLASS_NAME = [
  'rounded-full border border-white/[0.11] p-1.5 transition-all',
  'hover:border-white/[0.18]',
].join(' ');

const DASHBOARD_LEGACY_SECONDARY_ACTION_CLASS_NAME = [
  'rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60',
  'transition-colors hover:border-white/20 hover:text-white/80',
].join(' ');

const DASHBOARD_LEGACY_PRIMARY_ACTION_CLASS_NAME = [
  'rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white',
  'transition-opacity hover:opacity-90',
].join(' ');
const PROGRAMMATIC_SCROLL_GUARD_TIMEOUT_MS = 1_000;

/**
 * Invalidate every query key that backs a Dashboard game list.
 *
 * The Dashboard renders ONE of two list queries at a time
 * (`game:list` on the legacy path, `game:list:lite` on the lazy path —
 * see `useFeatureFlags().lazyDashboardLoad`). Mutations that change
 * the list contents (onboarding completion, game create / delete,
 * hero generation, community-game clone) need to refresh whichever
 * path is active. React Query's partial matching is index-by-index:
 * a single invalidate on `['game:list']` does NOT match
 * `['game:list:lite']` (the strings differ at index 0). Without
 * touching BOTH keys the lazy-path user sees stale data until they
 * navigate away and back.
 *
 * Folded post-merge on PR #706 — the bots called this out as
 * `Macroscope HIGH` / `Cursor MEDIUM` on the merged code, but the
 * inline review wasn't iterated before merge. See follow-up PR /
 * `feedback_github_bot_iteration.md` in the agent memory.
 */
export function invalidateGameLists(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['game:list'] });
  qc.invalidateQueries({ queryKey: ['game:list:lite'] });
}

function invalidateDashboardPreferences(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['dashboard:preferences:get'] });
}

function repoUrlFromFullName(repoFullName: string | undefined): string | undefined {
  return repoFullName ? `https://github.com/${repoFullName}` : undefined;
}

function openExternalUrl(url: string | undefined): void {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function localLiteToPreferenceGame(game: GameSummaryLite): DashboardLocalPreferenceGame {
  return {
    gameId: game.gameId,
    displayName: game.displayName,
    category: game.category,
    heroImage: game.heroImage,
    description: game.description,
    clonedFrom: game.clonedFrom,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
  };
}

function localSummaryToPreferenceGame(game: GameSummary): DashboardLocalPreferenceGame {
  return {
    gameId: game.gameId,
    displayName: game.name,
    category: game.category,
    heroImage: game.heroImage,
    description: game.description,
    clonedFrom: game.clonedFrom,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
  };
}

function localLitePreferencePayload(
  game: GameSummaryLite,
  scope: 'local' = 'local',
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope,
    gameId: game.gameId,
    displayName: game.displayName,
    repoFullName: game.clonedFrom,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
    heroImage: game.heroImage,
    description: game.description,
  };
}

function localSummaryPreferencePayload(
  game: GameSummary,
  scope: 'local' = 'local',
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope,
    gameId: game.gameId,
    displayName: game.name,
    repoFullName: game.clonedFrom,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
    heroImage: game.heroImage,
    description: game.description,
  };
}

function communityPreferencePayload(
  game: DiscoveredGame,
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope: 'community',
    gameId: game.gameId,
    displayName: game.displayName,
    repoFullName: game.repoFullName,
    repoUrl: game.repoUrl,
    heroImage: game.heroImage,
    description: game.description,
  };
}

/**
 * Issue #441 — orphan-repo cleanup card.
 *
 * Rendered separately from <CrucibleErrorState> because the warning
 * is a SECOND, non-fatal issue surfaced alongside the primary
 * create failure (the scaffold-rollback couldn't delete the empty
 * remote repo, so the user has an orphan on GitHub).
 *
 * Codex YELLOW #1 fold (PowerShell 5.1 portability): when the
 * `recovery` snippet contains multiple commands, the core emits
 * them on separate lines (joined by `\n`) because PS 5.1 doesn't
 * support `&&` chaining. The card renders each line as its own
 * `<code>` block so the user can copy each command individually —
 * a `select-all` on one block selects only that line. Single-line
 * recoveries fall through to the same rendering with a single
 * block, so the no-newline case still works exactly as before.
 *
 * Extracted as a named export so the SSR-only Dashboard test can
 * pin the two-block rendering contract without spinning up the
 * full Dashboard page (which depends on react-query, react-router,
 * IPC stubs, etc.).
 */
export function RollbackWarningCard({
  message,
  recovery,
}: {
  message: string;
  recovery?: string;
}): React.JSX.Element {
  const lines = recovery ? recovery.split('\n').filter((l) => l.length > 0) : [];
  return (
    <div
      data-testid="rollback-warning-card"
      className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
    >
      <p className="text-sm text-amber-100">{message}</p>
      {lines.length > 0 && (
        <>
          <p className="mt-2 text-xs text-amber-100/70">
            {lines.length > 1
              ? "Run these two commands in order in a terminal (PowerShell 5.1 doesn't support `&&`; the second only needs to run if the first succeeded):"
              : 'Run this in a terminal to clean up the orphan repo:'}
          </p>
          {lines.map((line, i) => (
            <code
              key={i}
              data-testid="rollback-warning-recovery-line"
              className="mt-1 block select-all break-all rounded bg-black/40 px-3 py-2 font-mono text-xs text-amber-50"
            >
              {line}
            </code>
          ))}
        </>
      )}
    </div>
  );
}

function DashboardSearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="relative flex items-center">
      <Search
        size={13}
        className="pointer-events-none absolute left-2.5"
        style={{ color: 'var(--text-3)' }}
      />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search games..."
        className="w-44 rounded-full border border-white/[0.10] bg-white/[0.05] py-1.5 pl-8 pr-7 text-[13px] placeholder-white/25 outline-none transition-colors focus:border-white/[0.20] focus:bg-white/[0.08]"
        style={{ color: 'var(--text-2)' }}
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
          className="absolute right-2 text-white/30 transition-colors hover:text-white/60"
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

export function Dashboard(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const frame = useAppFrame();
  const appModeQuery = useIpcQuery('app-mode:get-snapshot', undefined, {
    enabled: capabilities.hasLocalFilesystem,
    staleTime: 10_000,
  });
  const appModeSnapshot = appModeQuery.data;
  const hasConfirmedLocalAppMode =
    capabilities.hasLocalFilesystem &&
    appModeSnapshot !== undefined &&
    appModeSnapshot.mode !== 'cloud';
  const canImportLocalProject =
    hasConfirmedLocalAppMode && canInvokeChannel(capabilities, 'game:import:pick-directory');
  // Community is local-first; hide it only after app-mode explicitly reports Cloud.
  const showCommunityGames = capabilities.hasLocalFilesystem && appModeSnapshot?.mode !== 'cloud';
  // Dashboard lazy-load path (`docs/dashboard-lazy-load-plan.md`):
  // we now switch between two list queries based on the
  // `lazyDashboardLoad` flag. BOTH queries are gated on
  // `flags.loaded` so the renderer doesn't briefly fire the wrong
  // IPC during the first ~ms of mount (plan line 238).
  const flags = useFeatureFlags();
  const fullscreenFrame = frame === 'fullscreen';
  const { data: ghUser } = useGitHubUser();
  const lazyPath = flags.lazyDashboardLoad;
  const liteQuery = useIpcQuery('game:list:lite', { enabled: flags.loaded && lazyPath });
  const legacyQuery = useIpcQuery('game:list', { enabled: flags.loaded && !lazyPath });
  const preferencesQuery = useIpcQuery('dashboard:preferences:get');
  // Whichever path is active produces a normalised list view-model
  // the rest of the Dashboard renders. The lite path uses
  // `<LazyGameCard>` (per-card hydrate); the legacy path keeps the
  // existing `<GameCard>` shape — emergency rollback discipline.
  const lazyData: GameSummaryLite[] = lazyPath ? (liteQuery.data ?? []) : [];
  const legacyData: GameSummary[] = !lazyPath ? (legacyQuery.data ?? []) : [];
  const loading = lazyPath
    ? !flags.loaded || liteQuery.isLoading || preferencesQuery.isLoading
    : !flags.loaded || legacyQuery.isLoading || preferencesQuery.isLoading;
  const error = preferencesQuery.error ?? (lazyPath ? liteQuery.error : legacyQuery.error);
  const preferences = preferencesQuery.data;
  // CommunityGames deferral: `game:discover` competes with first-card
  // hydration for network. Only kick it off once the local list has
  // landed AND we're past first-paint idle (plan lines 199-201).
  // `idleReady` flips true on `requestIdleCallback` (or a 100ms
  // setTimeout fallback for Electron renderers that don't expose it)
  // after `listResolved` becomes true.
  const listResolved = lazyPath ? liteQuery.data !== undefined : legacyQuery.data !== undefined;
  const [idleReady, setIdleReady] = useState(false);
  useEffect(() => {
    if (!listResolved || idleReady) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const handle = w.requestIdleCallback(() => setIdleReady(true));
      return () => w.cancelIdleCallback?.(handle);
    }
    const timer = setTimeout(() => setIdleReady(true), 100);
    return () => clearTimeout(timer);
  }, [listResolved, idleReady]);
  const { data: onboardingDone } = useIpcQuery('app:onboarding-complete');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showNewGameDialog, setShowNewGameDialog] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Top-bar tabs. My Games + Community live on the SAME page (the tab scrolls
  // to the section); Archived swaps the content. Active tab is derived:
  // Archived when `showArchived`, else the last games-tab clicked.
  const [gamesTab, setGamesTab] = useState<'mine' | 'community'>('mine');
  const activeTab: 'mine' | 'community' | 'archived' = showArchived
    ? 'archived'
    : showCommunityGames
      ? gamesTab
      : 'mine';
  const mainRef = useRef<HTMLElement>(null);
  const myGamesRef = useRef<HTMLDivElement>(null);
  const communityRef = useRef<HTMLDivElement>(null);
  const programmaticScrollTargetRef = useRef<'mine' | 'community' | null>(null);
  const programmaticScrollGuardTimeoutRef = useRef<number | null>(null);

  /**
   * Clears the pending smooth-scroll target and its fallback timer.
   *
   * Args:
   *   None.
   *
   * Returns:
   *   None.
   *
   * Raises:
   *   None.
   */
  const clearProgrammaticScrollTarget = useCallback(() => {
    programmaticScrollTargetRef.current = null;
    if (programmaticScrollGuardTimeoutRef.current === null) return;
    window.clearTimeout(programmaticScrollGuardTimeoutRef.current);
    programmaticScrollGuardTimeoutRef.current = null;
  }, []);

  /**
   * Arms the smooth-scroll guard for a clicked Dashboard section tab.
   *
   * Args:
   *   tab: The section tab that should be reached by the programmatic scroll.
   *
   * Returns:
   *   None.
   *
   * Raises:
   *   None.
   */
  const armProgrammaticScrollTarget = useCallback(
    (tab: 'mine' | 'community') => {
      clearProgrammaticScrollTarget();
      programmaticScrollTargetRef.current = tab;
      // Layout can make the expected observer state unreachable, so do not guard forever.
      programmaticScrollGuardTimeoutRef.current = window.setTimeout(() => {
        if (programmaticScrollTargetRef.current === tab) {
          programmaticScrollTargetRef.current = null;
        }
        programmaticScrollGuardTimeoutRef.current = null;
      }, PROGRAMMATIC_SCROLL_GUARD_TIMEOUT_MS);
    },
    [clearProgrammaticScrollTarget],
  );

  const handleSelectTab = useCallback(
    (tab: 'mine' | 'community' | 'archived') => {
      if (tab === 'archived') {
        clearProgrammaticScrollTarget();
        setShowArchived(true);
        return;
      }
      if (tab === 'community' && !showCommunityGames) return;
      setShowArchived(false);
      armProgrammaticScrollTarget(tab);
      setGamesTab(tab);
      // Defer the scroll a frame so the (possibly just-unhidden) sections are
      // mounted before we scroll to them.
      requestAnimationFrame(() => {
        const target = tab === 'community' ? communityRef.current : myGamesRef.current;
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    [armProgrammaticScrollTarget, clearProgrammaticScrollTarget, showCommunityGames],
  );

  useEffect(() => {
    if (!showCommunityGames && gamesTab === 'community') {
      clearProgrammaticScrollTarget();
      setGamesTab('mine');
    }
  }, [clearProgrammaticScrollTarget, gamesTab, showCommunityGames]);

  useEffect(() => {
    return () => clearProgrammaticScrollTarget();
  }, [clearProgrammaticScrollTarget]);

  useEffect(() => {
    if (!flags.newUi || showArchived || !showCommunityGames) return;
    const root = mainRef.current;
    if (!root) return;
    const clearForUserInput = () => clearProgrammaticScrollTarget();
    // User wheel/touch/key input means manual scroll-spy updates should win again.
    root.addEventListener('wheel', clearForUserInput, { passive: true });
    root.addEventListener('touchmove', clearForUserInput, { passive: true });
    window.addEventListener('keydown', clearForUserInput);
    return () => {
      root.removeEventListener('wheel', clearForUserInput);
      root.removeEventListener('touchmove', clearForUserInput);
      window.removeEventListener('keydown', clearForUserInput);
    };
  }, [clearProgrammaticScrollTarget, flags.newUi, showArchived, showCommunityGames]);

  // Scroll-spy: keep the active tab in sync when the user MANUALLY scrolls
  // between sections. Once the Community section's top crosses into the top
  // slice of the scroll viewport it becomes "active"; scrolling back up flips
  // it to My Games. Skipped on the Archived view (the section anchors aren't
  // mounted) and where IntersectionObserver is unavailable (e.g. happy-dom).
  useEffect(() => {
    if (!flags.newUi || showArchived || !showCommunityGames) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const root = mainRef.current;
    const target = communityRef.current;
    if (!root || !target) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextTab = entry.isIntersecting ? 'community' : 'mine';
        const programmaticTarget = programmaticScrollTargetRef.current;
        if (programmaticTarget) {
          // Smooth scroll reports transient observer states before reaching the clicked section.
          if (nextTab !== programmaticTarget) return;
          clearProgrammaticScrollTarget();
        }
        setGamesTab(nextTab);
      },
      { root, rootMargin: '0px 0px -80% 0px', threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    clearProgrammaticScrollTarget,
    flags.newUi,
    showArchived,
    showCommunityGames,
    loading,
    error,
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: config } = useIpcQuery('config:get');
  const [editorError, setEditorError] = useState<string | null>(null);
  const editorErrorTimerRef = useRef<NodeJS.Timeout>(undefined);

  const openImportDialog = useCallback(() => {
    if (!canImportLocalProject) return;
    setShowNewGameDialog(false);
    setShowImportDialog(true);
  }, [canImportLocalProject]);

  useEffect(() => {
    if (!canImportLocalProject && showImportDialog) setShowImportDialog(false);
  }, [canImportLocalProject, showImportDialog]);

  const showTestGames = config?.showTestGames ?? false;

  // Normalise both code paths to share archive/favorite filtering and
  // the community duplicate-suppression local-id list.
  const localPreferenceGames = useMemo(
    () =>
      lazyPath
        ? lazyData.map(localLiteToPreferenceGame)
        : legacyData.map(localSummaryToPreferenceGame),
    [lazyPath, lazyData, legacyData],
  );
  const unarchivedLazy = useMemo(
    () =>
      filterDashboardLocalGames(lazyData, preferences, {
        showTestGames: true,
        searchQuery: '',
        getDisplayName: (g) => g.displayName,
      }),
    [lazyData, preferences],
  );
  const unarchivedLegacy = useMemo(
    () =>
      filterDashboardLocalGames(legacyData, preferences, {
        showTestGames: true,
        searchQuery: '',
        getDisplayName: (g) => g.name,
      }),
    [legacyData, preferences],
  );
  const filteredLazy = useMemo(
    () =>
      filterDashboardLocalGames(lazyData, preferences, {
        showTestGames,
        searchQuery: '',
        getDisplayName: (g) => g.displayName,
      }),
    [lazyData, preferences, showTestGames],
  );
  const filteredLegacy = useMemo(
    () =>
      filterDashboardLocalGames(legacyData, preferences, {
        showTestGames,
        searchQuery: '',
        getDisplayName: (g) => g.name,
      }),
    [legacyData, preferences, showTestGames],
  );
  const searchLazy = useMemo(
    () =>
      filterDashboardLocalGames(lazyData, preferences, {
        showTestGames,
        searchQuery,
        getDisplayName: (g) => g.displayName,
      }),
    [lazyData, preferences, showTestGames, searchQuery],
  );
  const searchLegacy = useMemo(
    () =>
      filterDashboardLocalGames(legacyData, preferences, {
        showTestGames,
        searchQuery,
        getDisplayName: (g) => g.name,
      }),
    [legacyData, preferences, showTestGames, searchQuery],
  );

  const totalCount = lazyPath ? unarchivedLazy.length : unarchivedLegacy.length;
  const filteredCount = lazyPath ? filteredLazy.length : filteredLegacy.length;
  const searchCount = lazyPath ? searchLazy.length : searchLegacy.length;
  const hiddenCount = showTestGames ? 0 : countHiddenTestGames(localPreferenceGames, preferences);
  const localGameIds = lazyPath ? lazyData.map((g) => g.gameId) : legacyData.map((g) => g.gameId);
  const communityEnabled = showCommunityGames && flags.loaded && listResolved && idleReady;
  const communityQuery = useIpcQuery('game:discover', { enabled: communityEnabled });
  const favoriteCards = useMemo(
    () =>
      resolveDashboardFavorites(preferences, {
        localGames: localPreferenceGames,
        communityGames: showCommunityGames ? (communityQuery.data ?? []) : [],
        getLocalDisplayName: (game) => game.displayName,
        showTestGames,
      }),
    [preferences, localPreferenceGames, communityQuery.data, showCommunityGames, showTestGames],
  );
  const archivedCount = preferences?.archived.length ?? 0;

  const setArchive = useCallback(
    async (input: DashboardArchiveSetInput): Promise<void> => {
      await window.crucible.invoke('game:archive:set', input);
      invalidateDashboardPreferences(queryClient);
      queryClient.invalidateQueries({ queryKey: ['game:discover'] });
    },
    [queryClient],
  );

  const setFavorite = useCallback(
    async (input: DashboardFavoriteSetInput): Promise<void> => {
      await window.crucible.invoke('game:favorite:set', input);
      invalidateDashboardPreferences(queryClient);
    },
    [queryClient],
  );

  // Show onboarding on first launch
  useEffect(() => {
    if (onboardingDone === false) setShowOnboarding(true);
  }, [onboardingDone]);

  // Subscribe to editor errors
  useEffect(() => {
    const unsub = window.crucible.on('editor:error', (msg) => {
      setEditorError(msg);
      clearTimeout(editorErrorTimerRef.current);
      editorErrorTimerRef.current = setTimeout(() => setEditorError(null), 5000);
    });
    return () => {
      unsub();
      clearTimeout(editorErrorTimerRef.current);
    };
  }, []);

  const dashboardActions = flags.newUi ? (
    <div className="flex items-center gap-2">
      <SwitchToCloudFoundryButton />
      <div
        className="flex items-center gap-1 rounded-full px-1 py-1"
        style={{ background: 'var(--bg-elev-1)' }}
      >
        <button
          onClick={() => navigate('/settings')}
          className={DASHBOARD_NEW_UI_SETTINGS_BUTTON_CLASS_NAME}
          style={{
            background: 'var(--color-crucible-bg)',
            color: 'var(--text-3)',
            transitionTimingFunction: 'var(--ease-flora)',
            transitionDuration: 'var(--motion-fast)',
          }}
          aria-label="Settings"
        >
          <SettingsIcon size={16} strokeWidth={1.75} />
        </button>
        {ghUser && (
          <img
            src={ghUser.avatarUrl}
            alt={ghUser.login}
            title={ghUser.login}
            className="h-8 w-8 rounded-full object-cover ring-1 ring-white/10"
          />
        )}
      </div>
    </div>
  ) : (
    <div className="flex gap-3">
      <SwitchToCloudFoundryButton />
      <button
        onClick={() => navigate('/devices')}
        className={DASHBOARD_LEGACY_SECONDARY_ACTION_CLASS_NAME}
      >
        Devices
      </button>
      <button
        onClick={() => navigate('/settings')}
        className={DASHBOARD_LEGACY_SECONDARY_ACTION_CLASS_NAME}
      >
        Settings
      </button>
      <button
        onClick={() => setShowNewGameDialog(true)}
        className={DASHBOARD_LEGACY_PRIMARY_ACTION_CLASS_NAME}
      >
        + New Game
      </button>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Editor error toast */}
      {editorError && (
        <div className="fixed right-4 top-4 z-40 max-w-sm rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm">
          {editorError}
        </div>
      )}

      {fullscreenFrame && (
        <FoundryShellHeader
          chrome="electron"
          // Title text hidden — a small LOCAL/CLOUD tag on the brand mark
          // conveys the mode instead.
          showBrandMark={flags.newUi}
          brandTag={
            flags.newUi
              ? appModeSnapshot?.mode === 'cloud' || !capabilities.hasLocalFilesystem
                ? 'CLOUD'
                : 'LOCAL'
              : undefined
          }
          actions={dashboardActions}
          // New UI: the persistent search/tabs bar below carries the only
          // divider, so drop the logo bar's bottom border to avoid a double rule.
          className={flags.newUi ? 'border-b-0' : undefined}
        />
      )}
      {flags.newUi && <UpdateBanner />}

      {/* Onboarding overlay (first launch) */}
      {showOnboarding && (
        <OnboardingOverlay
          onComplete={() => {
            setShowOnboarding(false);
            window.crucible.invoke('app:set-onboarding-complete');
            invalidateGameLists(queryClient);
          }}
        />
      )}

      {showNewGameDialog && (
        <NewGameChoiceDialog
          onClose={() => setShowNewGameDialog(false)}
          onCreate={() => {
            setShowNewGameDialog(false);
            setShowCreateDialog(true);
          }}
          onImport={openImportDialog}
          canImportLocalProject={canImportLocalProject}
        />
      )}

      {showCreateDialog && (
        <CreateGameDialog
          onClose={() => setShowCreateDialog(false)}
          onCreated={(gameId, routeState) => {
            setShowCreateDialog(false);
            navigate(`/workspace/${gameId}`, routeState ? { state: routeState } : undefined);
          }}
        />
      )}

      {canImportLocalProject && showImportDialog && (
        <ImportGameDialog
          canImportLocalProject={canImportLocalProject}
          onClose={() => setShowImportDialog(false)}
          onImported={(gameId) => {
            setShowImportDialog(false);
            navigate(`/workspace/${gameId}`);
          }}
        />
      )}

      {/* New UI: persistent top bar — pinned above the scroll area. Search on
          the left, the section tabs centred, the New Game button (and the
          settings/avatar actions when there's no app-shell header) on the
          right. */}
      {flags.newUi && (
        <div className="app-region-no-drag flex items-center gap-3 border-b border-white/[0.08] px-6 pb-3 pt-1">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <DashboardSearchBar value={searchQuery} onChange={setSearchQuery} />
            {hiddenCount > 0 && !showTestGames && (
              <span
                className="shrink-0 rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-white/30"
                title="Toggle in Settings → Preferences → Show test games"
              >
                {hiddenCount} test hidden
              </span>
            )}
          </div>
          <DashboardTabs
            active={activeTab}
            archivedCount={archivedCount}
            showCommunity={showCommunityGames}
            onSelect={handleSelectTab}
          />
          <div className="flex flex-1 items-center justify-end gap-2">
            <Button
              onClick={() => navigate('/new-game')}
              data-testid="dashboard-new-game-button"
              aria-label="New Game"
            >
              <Plus size={16} strokeWidth={2} />
              New Game
            </Button>
            {!fullscreenFrame && dashboardActions}
          </div>
        </div>
      )}

      {/* Content */}
      <main
        ref={mainRef}
        className={`flex-1 overflow-auto ${flags.newUi ? 'px-6 pt-4 pb-8 [&::-webkit-scrollbar]:hidden' : 'px-10 py-8'}`}
        style={flags.newUi ? { scrollbarWidth: 'none' } : undefined}
      >
        {/* Legacy header row — the new UI uses the persistent top bar above. */}
        {!flags.newUi && (
          <div className="mb-6 flex min-w-0 items-center gap-2.5 overflow-hidden">
            <h1 className="text-xl font-semibold">My Games</h1>
            <DashboardSearchBar value={searchQuery} onChange={setSearchQuery} />
            {hiddenCount > 0 && !showTestGames && (
              <span
                className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-white/30"
                title="Toggle in Settings → Preferences → Show test games"
              >
                {hiddenCount} test hidden
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowArchived((value) => !value)}
              className={`rounded-full border px-2 py-1 text-[11px] transition-colors ${
                showArchived
                  ? 'border-crucible-accent/40 bg-crucible-accent/15 text-crucible-accent'
                  : 'border-white/[0.10] bg-white/[0.04] text-white/35 hover:text-white/60'
              }`}
              title="View archived games"
            >
              Archived{archivedCount > 0 ? ` ${archivedCount}` : ''}
            </button>
            {!fullscreenFrame && (
              <div className="ml-auto flex shrink-0 items-center justify-end">
                {dashboardActions}
              </div>
            )}
          </div>
        )}

        {/* My Games — section title (new UI). Scroll anchor for the tab. */}
        {flags.newUi && !loading && !error && !showArchived && (
          <div ref={myGamesRef} className="pb-5 scroll-mt-4">
            <h2 className="f-title" style={{ color: 'var(--text-2)' }}>
              My Games
            </h2>
          </div>
        )}

        {loading && (
          <div className="flex h-full items-center justify-center text-white/40">
            Loading games...
          </div>
        )}

        {!loading && error && (
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <p className="text-sm text-red-400">Failed to load games</p>
            <p className="text-xs text-white/40">
              {error instanceof Error ? error.message : String(error)}
            </p>
          </div>
        )}

        {!loading && !error && showArchived && (
          <ArchivedGamesSection
            preferences={preferences}
            searchQuery={searchQuery}
            onRestore={(entry) => {
              void setArchive({
                scope: entry.scope,
                gameId: entry.gameId,
                repoFullName: entry.repoFullName,
                archived: false,
              });
            }}
          />
        )}

        {!loading && !error && !showArchived && favoriteCards.length > 0 && (
          <DashboardFavoritesSection
            favorites={favoriteCards}
            onOpenLocal={(gameId) => navigate(`/workspace/${gameId}`)}
            onClone={async (favorite) => {
              if (!favorite.repoFullName) return;
              await window.crucible.invoke('game:clone', {
                gameId: favorite.gameId,
                repoFullName: favorite.repoFullName,
              });
              invalidateGameLists(queryClient);
              queryClient.invalidateQueries({ queryKey: ['game:discover'] });
            }}
            onRemoveFavorite={(favorite) => {
              void setFavorite({
                scope: favorite.scope,
                gameId: favorite.gameId,
                repoFullName: favorite.repoFullName,
                favorite: false,
              });
            }}
          />
        )}

        {/* Legacy empty state — new UI uses the "+" tile inside the grid instead */}
        {!loading && !error && !showArchived && totalCount === 0 && !flags.newUi && (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <div className="text-6xl text-white/10">&#127918;</div>
            <h2 className="text-lg font-medium text-white/60">No games yet</h2>
            <p className="max-w-sm text-center text-sm text-white/40">
              Create a new game from a prompt, or import an existing local project.
            </p>
            <button
              onClick={() => setShowNewGameDialog(true)}
              className="mt-2 rounded-lg bg-crucible-accent px-6 py-3 font-medium text-white transition-opacity hover:opacity-90"
            >
              Add Your First Game
            </button>
          </div>
        )}

        {/* My Games empty state (new UI) — no games yet, no active search.
            A dedicated block rather than relying on the grid's "+" tile. */}
        {flags.newUi &&
          !loading &&
          !error &&
          !showArchived &&
          totalCount === 0 &&
          !searchQuery.trim() && (
            <div
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/[0.12] px-6 py-16 text-center"
              data-testid="dashboard-my-games-empty"
            >
              <h3 className="text-base font-medium" style={{ color: 'var(--text-2)' }}>
                No games yet
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-3)' }}>
                Got an idea? Let&apos;s bring it to life.
              </p>
              <Button
                onClick={() => navigate('/new-game')}
                data-testid="dashboard-empty-create-game-button"
                aria-label="Create New Game"
                className="mt-1"
              >
                <Plus size={16} strokeWidth={2} />
                Create New Game
              </Button>
            </div>
          )}

        {/* Game grid — rendered only when there are games to show. */}
        {!loading &&
          !error &&
          !showArchived &&
          searchCount > 0 &&
          (lazyPath
            ? renderLazyGrid(
                searchLazy,
                navigate,
                flags.newUi,
                preferences,
                setFavorite,
                setArchive,
                // New UI: no "+" grid tile — the persistent New Game button and
                // the empty-state CTA are the create affordances.
                flags.newUi ? undefined : () => setShowCreateDialog(true),
              )
            : renderLegacyGrid(
                searchLegacy,
                navigate,
                flags.newUi,
                preferences,
                setFavorite,
                setArchive,
                // New UI: no "+" grid tile — the persistent New Game button and
                // the empty-state CTA are the create affordances.
                flags.newUi ? undefined : () => setShowCreateDialog(true),
              ))}

        {/* Search found no matching games */}
        {!loading &&
          !error &&
          !showArchived &&
          searchQuery.trim() !== '' &&
          searchCount === 0 &&
          filteredCount > 0 && (
            <div className="flex h-48 flex-col items-center justify-center gap-2">
              <p className="text-sm text-white/40">No games matching &ldquo;{searchQuery}&rdquo;</p>
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-xs text-crucible-accent transition-opacity hover:opacity-80"
              >
                Clear search
              </button>
            </div>
          )}

        {!loading &&
          !error &&
          !showArchived &&
          totalCount > 0 &&
          filteredCount === 0 &&
          !searchQuery.trim() && (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <div className="text-4xl text-white/10">&#128065;</div>
              <h2 className="text-lg font-medium text-white/60">All games are hidden</h2>
              <p className="max-w-sm text-center text-sm text-white/40">
                {hiddenCount} test game{hiddenCount === 1 ? ' is' : 's are'} hidden. Enable "Show
                test games" in Settings to reveal them.
              </p>
            </div>
          )}

        {/* Community Games — all games in the org. Deferred until the
            local list has resolved so `game:discover` doesn't fight
            first-card hydration for network. Plan lines 199-201. */}
        {showCommunityGames && !showArchived && (
          <div ref={communityRef} className="scroll-mt-4">
            <CommunityGames
              localGameIds={localGameIds}
              showTestGames={showTestGames}
              discovered={communityQuery.data}
              isLoading={communityQuery.isLoading}
              error={communityQuery.error}
              preferences={preferences}
              onArchive={setArchive}
              onFavorite={setFavorite}
              newUi={flags.newUi}
              searchQuery={searchQuery}
            />
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-bar section tabs (new UI). My Games / Community scroll to their section
// on the same page; Archived swaps the content. Styled with the shared
// `.mode-pill` class so they match the game-editor mode pills exactly.
// ---------------------------------------------------------------------------

type DashboardTabId = 'mine' | 'community' | 'archived';

function DashboardTabs({
  active,
  archivedCount,
  showCommunity,
  onSelect,
}: {
  active: DashboardTabId;
  archivedCount: number;
  showCommunity: boolean;
  onSelect: (tab: DashboardTabId) => void;
}): React.JSX.Element {
  const tabs: Array<{ id: DashboardTabId; label: string; Icon: typeof Flame }> = [
    { id: 'mine', label: 'My Games', Icon: Flame },
    ...(showCommunity ? [{ id: 'community' as const, label: 'Community', Icon: Globe }] : []),
    { id: 'archived', label: 'Archived', Icon: Archive },
  ];
  return (
    <div
      role="tablist"
      aria-label="Dashboard sections"
      className="app-region-no-drag flex h-10 items-center gap-0.5 rounded-full px-1"
      style={{
        background: 'var(--surface-mode-pill-bar)',
        boxShadow: 'var(--surface-mode-pill-bar-ring)',
      }}
    >
      {tabs.map(({ id, label, Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-active={isActive ? 'true' : 'false'}
            data-testid={`dashboard-tab-${id}`}
            onClick={() => onSelect(id)}
            className="mode-pill"
          >
            <Icon size={14} strokeWidth={1.75} />
            {label}
            {id === 'archived' && archivedCount > 0 && (
              <span className="text-white/40">{archivedCount}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New-game "plus" tile — first item in the game grid (new UI only).
// ---------------------------------------------------------------------------

/**
 * A blank tile shaped like a game card with a centered plus icon.
 * Appears first in the grid and triggers the create-game dialog.
 * Replaces the old header "+ New Game" button and the old empty-state
 * CTA in the new UI.
 */
function NewGameTile({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Create a new game"
      className="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent"
      style={{
        backgroundColor: 'var(--bg-panel)',
      }}
    >
      {/* Pulsing accent glow. The create-game action is the only entry
          point in the new UI and was easy to miss as a plain dark tile,
          so the border + halo gently pulse to draw the eye. `animate-pulse`
          eases the whole layer's opacity; the hover rule pins it solid so
          it stops pulsing once the user is clearly on it. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 animate-pulse rounded-2xl group-hover:!opacity-100"
        style={{
          boxShadow: 'inset 0 0 0 1px rgba(124,111,247,0.7), 0 0 22px 1px rgba(124,111,247,0.40)',
        }}
      />

      {/* Ghost spacers mirror the LazyGameCard structure so the tile
          grows to the same intrinsic height at every grid column width —
          regardless of which row it lands in. No explicit px height needed. */}
      <div className="aspect-video w-full" />
      {/* Footer spacer: p-3 (12px top+bottom) + h-6 avatar row = ~48px */}
      <div className="p-3">
        <div className="h-6" />
      </div>

      {/* Accent plus + label — absolutely centred over the ghost
          structure. The circle pulses with the halo and the explicit
          "New game" label removes any doubt about what the tile does. */}
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <span
          className="flex h-12 w-12 animate-pulse items-center justify-center rounded-full
            transition-transform duration-[var(--motion-fast)] group-hover:scale-110 group-hover:!opacity-100"
          style={{
            background: 'rgba(124,111,247,0.18)',
            boxShadow: 'inset 0 0 0 1px rgba(124,111,247,0.55)',
          }}
        >
          <Plus
            size={18}
            strokeWidth={2}
            style={{ color: 'var(--color-crucible-accent, #7C6FF7)' }}
          />
        </span>
        <span
          className="text-[11px] font-medium tracking-wide"
          style={{ color: 'var(--color-crucible-accent, #7C6FF7)' }}
        >
          New game
        </span>
      </span>
    </button>
  );
}

export function DashboardFavoritesSection({
  favorites,
  onOpenLocal,
  onClone,
  onRemoveFavorite,
}: {
  favorites: DashboardFavoriteCard[];
  onOpenLocal: (gameId: string) => void;
  onClone: (favorite: DashboardFavoriteCard) => Promise<void>;
  onRemoveFavorite: (favorite: DashboardFavoriteCard) => void;
}): React.JSX.Element {
  const [cloningKey, setCloningKey] = useState<string | null>(null);
  const [failedHeroImageKeys, setFailedHeroImageKeys] = useState<Set<string>>(() => new Set());

  return (
    <section className="mb-8" aria-label="Favorite games">
      <div className="mb-4 flex items-center gap-2">
        <Star size={15} className="text-[#FFDA0A]" fill="currentColor" />
        <h2 className="f-title" style={{ color: 'var(--text-2)' }}>
          Favorites
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {favorites.map((favorite) => {
          const cloning = cloningKey === favorite.key;
          const heroFailureKey =
            favorite.heroImage !== undefined
              ? `${favorite.key}:${favorite.heroImage}`
              : favorite.key;
          const heroImageFailed = failedHeroImageKeys.has(heroFailureKey);
          return (
            <article
              key={favorite.key}
              className="group overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.035] transition-colors hover:bg-white/[0.055]"
            >
              <div
                role={favorite.source === 'local' ? 'button' : undefined}
                tabIndex={favorite.source === 'local' ? 0 : undefined}
                onClick={() => {
                  if (favorite.source === 'local') onOpenLocal(favorite.gameId);
                }}
                onKeyDown={(e) => {
                  if (favorite.source !== 'local') return;
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenLocal(favorite.gameId);
                  }
                }}
                className={favorite.source === 'local' ? 'cursor-pointer' : undefined}
                aria-label={
                  favorite.source === 'local' ? `Open ${favorite.displayName}` : undefined
                }
              >
                <div className="relative aspect-video bg-white/[0.04]">
                  {favorite.heroImage && !heroImageFailed ? (
                    <img
                      src={favorite.heroImage}
                      alt={favorite.displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={() => {
                        setFailedHeroImageKeys((prev) => {
                          if (prev.has(heroFailureKey)) return prev;
                          const next = new Set(prev);
                          next.add(heroFailureKey);
                          return next;
                        });
                      }}
                    />
                  ) : (
                    <GenreFallback
                      genreKey={inferGenreKey(favorite.displayName, favorite.description)}
                      alt={favorite.displayName}
                      className="h-full w-full object-cover"
                    />
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFavorite(favorite);
                    }}
                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-[#FFDA0A] backdrop-blur-sm transition-colors hover:bg-black/80"
                    title="Remove from favorites"
                    aria-label={`Unfavorite ${favorite.displayName}`}
                  >
                    <Star size={14} fill="currentColor" strokeWidth={1.8} />
                  </button>
                </div>
                <div className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium text-white/85">
                        {favorite.displayName}
                      </h3>
                      <p className="text-[11px] text-white/35">
                        {favorite.source === 'local'
                          ? favorite.repoFullName
                            ? 'Local copy'
                            : 'Local game'
                          : favorite.source === 'community'
                            ? 'Community game'
                            : 'Saved favorite'}
                      </p>
                    </div>
                  </div>
                  {favorite.description && (
                    <p className="line-clamp-2 text-xs text-white/40">{favorite.description}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 px-3 pb-3">
                {favorite.source === 'local' ? (
                  <button
                    type="button"
                    onClick={() => onOpenLocal(favorite.gameId)}
                    className="rounded-full bg-white/[0.06] px-2.5 py-1 text-xs text-white/60 transition-colors hover:bg-white/[0.10] hover:text-white/80"
                  >
                    Open
                  </button>
                ) : favorite.repoFullName ? (
                  <button
                    type="button"
                    disabled={cloning}
                    onClick={async () => {
                      setCloningKey(favorite.key);
                      try {
                        await onClone(favorite);
                      } finally {
                        setCloningKey(null);
                      }
                    }}
                    className="rounded-full bg-crucible-accent/20 px-2.5 py-1 text-xs font-medium text-crucible-accent transition-colors hover:bg-crucible-accent/30 disabled:opacity-40"
                  >
                    {cloning ? 'Cloning...' : 'Clone to edit'}
                  </button>
                ) : null}
                {favorite.repoUrl && (
                  <button
                    type="button"
                    onClick={() => openExternalUrl(favorite.repoUrl)}
                    className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] px-2.5 py-1 text-xs text-white/50 transition-colors hover:bg-white/[0.09] hover:text-white/75"
                  >
                    <ExternalLink size={12} strokeWidth={1.8} />
                    GitHub
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ArchivedGamesSection({
  preferences,
  searchQuery,
  onRestore,
}: {
  preferences: DashboardPreferences | undefined;
  searchQuery: string;
  onRestore: (entry: DashboardArchiveEntry) => void;
}): React.JSX.Element {
  const query = searchQuery.trim().toLowerCase();
  const archived = (preferences?.archived ?? [])
    .filter((entry) => {
      if (!query) return true;
      const label = entry.displayName ?? entry.repoFullName ?? entry.gameId;
      return label.toLowerCase().includes(query);
    })
    .sort((a, b) => (a.displayName ?? a.gameId).localeCompare(b.displayName ?? b.gameId));

  if (archived.length === 0) {
    return (
      <section className="flex h-64 flex-col items-center justify-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.025]">
        <Archive size={24} className="text-white/20" />
        <h2 className="text-sm font-medium text-white/55">No archived games</h2>
        <p className="text-xs text-white/35">
          {query
            ? 'No archived games match your search.'
            : 'Archived games will appear here so you can restore them.'}
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Archived games">
      <div className="mb-4 flex items-center gap-2">
        <Archive size={15} className="text-white/45" />
        <h2 className="f-title" style={{ color: 'var(--text-2)' }}>
          Archived Games
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {archived.map((entry) => {
          const label = entry.displayName ?? entry.repoFullName ?? entry.gameId;
          return (
            <article
              key={dashboardPreferenceKey(entry)}
              className="overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.035]"
            >
              <div className="aspect-video bg-white/[0.04]">
                {entry.heroImage ? (
                  <img
                    src={entry.heroImage}
                    alt={label}
                    className="h-full w-full object-cover opacity-70"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <GenreFallback
                    genreKey={inferGenreKey(label, entry.description)}
                    alt={label}
                    className="h-full w-full object-cover opacity-70"
                  />
                )}
              </div>
              <div className="space-y-3 p-3">
                <div>
                  <h3 className="truncate text-sm font-medium text-white/75">{label}</h3>
                  <p className="text-[11px] text-white/35">
                    {entry.scope === 'community' ? 'Community game' : 'Local game'} · archived{' '}
                    {formatTimeAgo(entry.archivedAt)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onRestore(entry)}
                    className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-2.5 py-1 text-xs text-white/65 transition-colors hover:bg-white/[0.11] hover:text-white/85"
                  >
                    <RotateCcw size={12} strokeWidth={1.8} />
                    Restore
                  </button>
                  {entry.repoUrl && (
                    <button
                      type="button"
                      onClick={() => openExternalUrl(entry.repoUrl)}
                      className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] px-2.5 py-1 text-xs text-white/50 transition-colors hover:bg-white/[0.09] hover:text-white/75"
                    >
                      <ExternalLink size={12} strokeWidth={1.8} />
                      GitHub
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Renders the legacy `<GameCard>` grid driven by the full `GameSummary`
 * payload from the eager `game:list` IPC. Kept verbatim from the
 * pre-lazy Dashboard so the rollback path
 * (`CRUCIBLE_LAZY_DASHBOARD_LOAD=0`) is a true one-knob switch — no
 * behavioural drift between code paths.
 */
function renderLegacyGrid(
  filteredGames: GameSummary[],
  navigate: ReturnType<typeof useNavigate>,
  newUi: boolean,
  preferences: DashboardPreferences | undefined,
  onFavorite: (input: DashboardFavoriteSetInput) => Promise<void>,
  onArchive: (input: DashboardArchiveSetInput) => Promise<void>,
  onCreateGame?: () => void,
): React.JSX.Element {
  // Glint system: only the three most-recently-updated cards
  // get the edge-trace ambient highlight (per the design spec).
  // Sort by lastModified desc; cards with no usable timestamp
  // (`""` or unparseable) are treated as oldest so they never
  // crowd out a real recent edit. Stagger the animation phase
  // via data-fresh-index 0/1/2.
  const ts = (g: GameSummary): number => {
    const v = g.lastModified;
    if (!v) return 0;
    const parsed = new Date(v).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const sortedByRecency = [...filteredGames].sort((a, b) => ts(b) - ts(a));
  // Only mark a card "fresh" if it has a real timestamp —
  // otherwise the top three cards in render order would all
  // glow on a fresh `crucible list` where nothing's been
  // edited.
  const freshIds = new Map(
    sortedByRecency
      .slice(0, 3)
      .filter((g) => ts(g) > 0)
      .map((g, i) => [g.gameId, i] as const),
  );
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {newUi && onCreateGame && (
        <div className="relative grid rounded-2xl">
          <NewGameTile onClick={onCreateGame} />
        </div>
      )}
      {[...filteredGames]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((game) => {
          const freshIndex = freshIds.get(game.gameId);
          const freshProps =
            freshIndex !== undefined
              ? { 'data-fresh': 'true', 'data-fresh-index': String(freshIndex) }
              : {};
          return (
            <div
              key={game.gameId}
              className={`relative grid ${newUi ? 'rounded-2xl' : 'rounded-lg'}`}
              {...freshProps}
            >
              <GameCard
                game={game}
                onClick={() => navigate(`/workspace/${game.gameId}`)}
                newUi={newUi}
                favorite={isDashboardFavorited(preferences, {
                  scope: 'local',
                  gameId: game.gameId,
                })}
                onFavorite={(favorite) =>
                  onFavorite({ ...localSummaryPreferencePayload(game), favorite })
                }
                onArchive={() =>
                  onArchive({ ...localSummaryPreferencePayload(game), archived: true })
                }
              />
            </div>
          );
        })}
    </div>
  );
}

/**
 * Renders the lazy `<LazyGameCard>` grid driven by `GameSummaryLite`.
 * Each card mounts in placeholder state and hydrates its per-card
 * status + badges only when it scrolls into view (200 px rootMargin).
 * Plan §"Layer 2 — Per-card hydration".
 *
 * Glint "freshness" is sorted by `createdAt` here — `lastModified`
 * isn't on the lite payload (it would force a per-game `fs.stat` in
 * the cheap-fast list, defeating the point). `createdAt` is a
 * reasonable proxy for "newest cards glow"; once a user scrolls to a
 * card and the lite-status hydrate lands we don't retroactively
 * reorder the glint set — keeping the visual stable mid-scroll
 * matters more than precise recency.
 */
function renderLazyGrid(
  filteredGames: GameSummaryLite[],
  navigate: ReturnType<typeof useNavigate>,
  newUi: boolean,
  preferences: DashboardPreferences | undefined,
  onFavorite: (input: DashboardFavoriteSetInput) => Promise<void>,
  onArchive: (input: DashboardArchiveSetInput) => Promise<void>,
  onCreateGame?: () => void,
): React.JSX.Element {
  const ts = (g: GameSummaryLite): number => {
    const v = g.createdAt;
    if (!v) return 0;
    const parsed = new Date(v).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const sortedByRecency = [...filteredGames].sort((a, b) => ts(b) - ts(a));
  const freshIds = new Map(
    sortedByRecency
      .slice(0, 3)
      .filter((g) => ts(g) > 0)
      .map((g, i) => [g.gameId, i] as const),
  );
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {newUi && onCreateGame && (
        <div className="relative grid rounded-2xl">
          <NewGameTile onClick={onCreateGame} />
        </div>
      )}
      {[...filteredGames]
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((game) => {
          const freshIndex = freshIds.get(game.gameId);
          const freshProps =
            freshIndex !== undefined
              ? { 'data-fresh': 'true', 'data-fresh-index': String(freshIndex) }
              : {};
          return (
            <div
              key={game.gameId}
              className={`relative grid ${newUi ? 'rounded-2xl' : 'rounded-lg'}`}
              {...freshProps}
            >
              <LazyGameCard
                game={game}
                onClick={() => navigate(`/workspace/${game.gameId}`)}
                newUi={newUi}
                favorite={isDashboardFavorited(preferences, {
                  scope: 'local',
                  gameId: game.gameId,
                })}
                onFavorite={(favorite) =>
                  onFavorite({ ...localLitePreferencePayload(game), favorite })
                }
                onArchive={() => onArchive({ ...localLitePreferencePayload(game), archived: true })}
              />
            </div>
          );
        })}
    </div>
  );
}

const STATUS_COLOURS: Record<GameSummary['status'], string> = {
  draft: 'bg-white/20 text-white/60',
  building: 'bg-[#2145F7]/20 text-[#4d6ef9]',
  prototype: 'bg-[#FFDA0A]/20 text-[#FFDA0A]',
  live: 'bg-[#588F3D]/20 text-[#7ab856]',
  error: 'bg-[#F72149]/20 text-[#f9506f]',
};

/** Pill overlay config for new-UI tile images. */
const STATUS_PILL: Record<GameStatusBadge, { label: string; dot: string; text: string }> = {
  draft: { label: 'Draft', dot: 'bg-white/40', text: 'text-white/60' },
  building: { label: 'Building', dot: 'bg-[#2145F7]', text: 'text-[#4d6ef9]' },
  prototype: { label: 'Prototype', dot: 'bg-[#FFDA0A]', text: 'text-[#FFDA0A]' },
  live: { label: 'Live', dot: 'bg-[#588F3D]', text: 'text-[#7ab856]' },
  error: { label: 'Error', dot: 'bg-[#F72149]', text: 'text-[#f9506f]' },
};

/**
 * Issue #462 — the GameCard used to wrap the whole card surface in
 * a native `<button>`, which silently nested SIX inner
 * `<div role="button">` action affordances (generate-artwork,
 * open-in-cursor, delete, status, TemplateUpdateBadge,
 * InterviewInProgressBadge) inside an interactive ancestor with the
 * same role. That's an HTML / WCAG violation: an interactive element
 * cannot have interactive descendants with the same `button` role.
 * Screen readers report the nested-role conflict and keyboard
 * navigation gets confused; some browsers strip the inner buttons'
 * click handlers.
 *
 * The fix unflattens the hierarchy. The OUTER card surface (the "open
 * this game" affordance) is now a `<div role="button" tabIndex={0}>`
 * with explicit `onClick` + `onKeyDown` Enter/Space handlers. The
 * inner action affordances remain as they were (each
 * `stopPropagation`s its own click so the card doesn't open). All
 * keyboard a11y survives: the outer card is tab-focusable; pressing
 * Enter or Space opens the workspace.
 *
 * Exported so the dedicated a11y regression test
 * (`Dashboard.gamecard-a11y.test.tsx`) can mount this in isolation
 * without dragging the whole Dashboard's IPC + router surface in.
 */
/**
 * Lite card surface for the dashboard lazy-load path. Mounts in
 * placeholder state from a `GameSummaryLite` payload (gameId +
 * displayName + createdAt only) and hydrates the rest only when the
 * card scrolls into view (`useCardVisibility` with 200 px rootMargin).
 *
 * Plan: `docs/dashboard-lazy-load-plan.md` §"Layer 2 — Per-card
 * hydration". The three badges (`InstallStateChip`,
 * `TemplateUpdateBadge`, `InterviewInProgressBadge`) and the status-
 * lite hydrate all share the same `isVisible` boolean — cards below
 * the fold stay inert; scrolling near them flips the flag and every
 * child query fires at once.
 *
 * Hero image: synthesised from the `game-asset://` protocol (no IPC
 * needed; the protocol handler resolves disk paths server-side) and
 * tagged `loading="lazy"` so the browser defers off-viewport fetches.
 * Falls back to `<GenreFallback>` if the asset 404s.
 */
/** Green circle icon button shown on new-UI tiles when a template update is available. */
function UpdateIconButton({
  gameId,
  enabled,
  onClick,
}: {
  gameId: string;
  enabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
}): React.JSX.Element | null {
  const { data } = useTemplateUpdateCheck(gameId, { enabled });
  if (!data?.hasUpdate) return null;
  const tone = data.isBreaking
    ? 'bg-[#FB7928]/20 text-[#FB7928] hover:bg-[#FB7928]/30'
    : 'bg-[#BCE107]/20 text-[#BCE107] hover:bg-[#BCE107]/30';
  return (
    <div
      role="button"
      tabIndex={0}
      title={
        data.isBreaking
          ? `Breaking update: v${data.current} → v${data.latest}`
          : `Update available: v${data.current} → v${data.latest}`
      }
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent);
        }
      }}
      className={`shrink-0 cursor-pointer rounded-full p-1.5 transition-colors ${tone}`}
    >
      {data.isBreaking ? (
        <AlertTriangle size={12} strokeWidth={2.5} />
      ) : (
        <RefreshCw size={12} strokeWidth={2.5} />
      )}
    </div>
  );
}

export function LazyGameCard({
  game,
  onClick,
  newUi = false,
  favorite = false,
  onFavorite,
  onArchive,
}: {
  game: GameSummaryLite;
  onClick: () => void;
  newUi?: boolean;
  favorite?: boolean;
  onFavorite?: (favorite: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
}): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const canOpenInEditor =
    capabilities.hasLocalFilesystem && canInvokeChannel(capabilities, 'game:open-in-editor');
  const canGenerateHero = canInvokeChannel(capabilities, 'game:generate-hero');
  const cardRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useCardVisibility(cardRef, { rootMargin: '200px' });
  const status = useGameStatusLite(game.gameId, { enabled: isVisible });
  const statusBadge: GameStatusBadge = status.data?.statusBadge ?? 'draft';
  // Mirrors legacy GameCard: the TemplateUpdateBadge needs a click
  // target that opens the UpdateTemplateModal. Without this state +
  // the rendered modal below, the badge's required `onClick` prop has
  // no useful destination and the user has no way to act on a flagged
  // template drift on the lazy path.
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  // Hover-only action affordances — same set the legacy `<GameCard>`
  // ships (favorite, generate artwork, open in cursor, archive/delete). The
  // lazy card initially shipped without these and the dashboard's
  // affordances regressed on the lazy path: no way to regenerate art,
  // open the folder in an editor, or hide/remove a local clone. Restored
  // for parity. State mirrors legacy: `generating` gates the
  // generate-hero IPC against double-clicks, `genError` shows a
  // 5-second toast on failure.
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  // Hero URL is resolved on the main side and carried on the lite
  // payload (`GameSummaryLite.heroImage`) — same shape the legacy
  // `<GameCard>` consumes from `game:list`. The earlier code in this
  // function tried to synthesise `game-asset://<id>/hero.webp` from
  // the renderer, but the protocol handler in main/index.ts expects
  // an encoded absolute path (line 5448), not a `<id>/<file>`
  // relative, so every hero 403'd on the lazy path. Resolving in
  // main keeps the protocol contract honest and avoids duplicating
  // the bundled-tile / on-disk / genre-fallback chain in the
  // renderer.
  const [imgFailed, setImgFailed] = useState(false);
  const heroUrl = game.heroImage;

  const handleOpenInCursor = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!canOpenInEditor) return;
    // `game:open-in-editor` emits `editor:error` over a separate IPC
    // for editor-not-found cases (the dashboard-level toast effect
    // at the top of this file catches those). Other rejections — the
    // M-7 `assertValidGameId` throw added in a1bd9d0, a
    // `getGamesDir()` failure, an editor spawn error — bypass that
    // channel and would otherwise surface as unhandled rejections.
    // Catch and warn so the console shows the failure but we don't
    // double up on user-visible toasts. Cursor Bugbot LOW on f00b178.
    void window.crucible
      .invoke('game:open-in-editor', game.gameId)

      .catch((err) => console.warn('[game:open-in-editor] failed:', err));
  };

  const handleGenerateHero = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (generating || !canGenerateHero) return;
    setGenerating(true);
    setGenError(null);
    try {
      await window.crucible.invoke('game:generate-hero', {
        gameId: game.gameId,
        gameName: game.displayName,
      });
      // Touch BOTH list queries so the lazy path picks up the new
      // hero URL on its next paint, same as the legacy path would.
      invalidateGameLists(queryClient);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setGenError(msg);
      setTimeout(() => setGenError(null), 5000);
    } finally {
      setGenerating(false);
    }
  };

  const handleFavorite = (e: React.MouseEvent): void => {
    e.stopPropagation();
    void onFavorite?.(!favorite);
  };

  const handleArchive = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (
      confirm(
        `Archive "${game.displayName}"? It will be hidden from the Dashboard until you restore it. Files stay on disk.`,
      )
    ) {
      void onArchive?.();
    }
  };

  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const prompt = game.imported
      ? `Remove "${game.displayName}" from Foundry? This keeps the project files on disk.`
      : `Delete "${game.displayName}" locally? This removes the local files but keeps the GitHub repo.`;
    if (confirm(prompt)) {
      // `game:delete` can reject for the same reasons as the editor
      // open above (validation throw, FS failure). Catch so a delete
      // failure doesn't surface as an unhandled rejection; the user
      // will retry when they notice the card is still in the grid.
      window.crucible.invoke('game:delete', { gameId: game.gameId }).then(
        () => invalidateGameLists(queryClient),

        (err) => console.warn('[game:delete] failed:', err),
      );
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleCardKeyDown}
        data-testid="gamecard-surface"
        aria-label={`Open ${game.displayName}`}
        className={
          newUi
            ? 'card-gradient group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl transition-all hover:brightness-[1.08]'
            : 'group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-white/10 bg-white/[0.03] transition-all hover:border-white/20 hover:bg-white/[0.05]'
        }
        style={
          newUi
            ? {
                boxShadow: 'var(--hairline), var(--shadow-card)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }
            : undefined
        }
      >
        <div className="relative aspect-video w-full bg-white/5">
          {heroUrl && !imgFailed ? (
            <img
              src={heroUrl}
              alt={game.displayName}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <GenreFallback
              genreKey={inferGenreKey(game.displayName, game.description)}
              alt={game.displayName}
              className="h-full w-full object-cover"
            />
          )}
          {generating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              <span className="text-xs font-medium text-white/70">Generating artwork...</span>
            </div>
          )}
          {genError && (
            <div className="absolute inset-x-0 bottom-0 bg-red-900/90 px-3 py-2 text-xs text-red-200 backdrop-blur-sm">
              {genError}
            </div>
          )}
          {/* Status pill — new UI only, always visible, hover controls overlay on top */}
          {newUi &&
            (() => {
              const p = STATUS_PILL[statusBadge];
              return (
                <div
                  className={`absolute left-2 top-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 backdrop-blur-sm ${p.text}`}
                  style={{ background: 'rgba(10,10,10,0.75)' }}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${p.dot}`} />
                  <span className="text-[11px] font-medium leading-none">{p.label}</span>
                </div>
              );
            })()}
        </div>
        {onFavorite && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleFavorite}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleFavorite(e as unknown as React.MouseEvent);
              }
            }}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={
              favorite ? `Unfavorite ${game.displayName}` : `Favorite ${game.displayName}`
            }
            className={`${newUi ? 'left-2 top-11' : 'left-2 top-2'} absolute flex h-7 w-7 items-center justify-center rounded-md bg-black/60 ${favorite ? 'text-[#FFDA0A]' : 'text-white/50'} opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-[#FFDA0A] group-hover:opacity-100`}
          >
            <Star size={14} strokeWidth={1.8} fill={favorite ? 'currentColor' : 'none'} />
          </div>
        )}
        {/* Hover action affordances — mirror legacy GameCard L688-L761
            verbatim so the lazy path doesn't regress card-level
            affordances (generate art, open in cursor, archive/delete). Each
            is `<div role="button">` (nested role inside the outer
            card's role="button"); each `stopPropagation`s so the
            card-open doesn't fire behind it. opacity-0 →
            group-hover:opacity-100 keeps the surface clean until the
            mouse lands. */}
        {/* Legacy UI: generate-hero button at top-left. New UI uses the
            same spot for the status pill above, so suppress this
            affordance under the newUi gate. */}
        {!newUi && !game.imported && canGenerateHero && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                // `preventDefault` blocks the Space-bar page-scroll the
                // browser would otherwise trigger on a focused role=button.
                // Macroscope MED on eb1de4a.
                e.preventDefault();
                e.stopPropagation();
                handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.displayName}`
                : `Generate artwork for ${game.displayName}`
            }
            className={`absolute ${onFavorite ? 'left-2 top-11' : 'left-2 top-2'} flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100`}
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}
        {canOpenInEditor && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleOpenInCursor}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenInCursor(e as unknown as React.MouseEvent);
              }
            }}
            title="Open in Cursor"
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 2L2 8l3 6M11 2l3 6-3 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M8 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        )}
        {/* New UI: generate-hero button at right-2 top-11. Legacy
            UI keeps main's delete button in this spot — see below. */}
        {newUi && canGenerateHero && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.displayName}`
                : `Generate artwork for ${game.displayName}`
            }
            className="absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}
        {/* Legacy UI: archive/delete action at right-2 top-11 (smart
            label — Archive game when preferences are available, otherwise
            Remove from Foundry for imported or Delete locally). Wrapped in
            !newUi so the new UI's generate-
            hero button takes the same spot without overlap. */}
        {newUi && onArchive && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleArchive}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleArchive(e as unknown as React.MouseEvent);
              }
            }}
            title="Archive game"
            aria-label={`Archive ${game.displayName}`}
            className="absolute right-2 top-20 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          >
            <Archive size={14} strokeWidth={1.8} />
          </div>
        )}
        {!newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={onArchive ? handleArchive : handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                (onArchive ? handleArchive : handleDelete)(e as unknown as React.MouseEvent);
              }
            }}
            title={
              onArchive ? 'Archive game' : game.imported ? 'Remove from Foundry' : 'Delete locally'
            }
            aria-label={
              onArchive
                ? `Archive ${game.displayName}`
                : game.imported
                  ? `Remove ${game.displayName} from Foundry`
                  : `Delete ${game.displayName} locally`
            }
            className={`absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity ${onArchive ? 'hover:bg-black/80 hover:text-white/90' : 'hover:bg-red-900/80 hover:text-red-300'} group-hover:opacity-100`}
          >
            {onArchive ? (
              <Archive size={14} strokeWidth={1.8} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        )}
        <div className="flex flex-1 flex-col gap-2 p-3">
          {newUi ? (
            <>
              <div className="flex items-start gap-2">
                <h3
                  className="min-w-0 flex-1 truncate text-sm leading-6"
                  style={{ color: 'var(--text-1)', fontWeight: 500 }}
                >
                  {game.displayName}
                </h3>
                <div className="flex shrink-0 items-center gap-1">
                  <InstallStateChip gameId={game.gameId} variant="icon" enabled={isVisible} />
                  <UpdateIconButton
                    gameId={game.gameId}
                    enabled={isVisible}
                    onClick={() => setShowUpdateModal(true)}
                  />
                </div>
              </div>
              <div className="flex min-h-6 items-center gap-2">
                {/* Contributor facepile */}
                {game.contributors && game.contributors.length > 0 ? (
                  <div className="flex shrink-0 items-center">
                    {game.contributors.slice(0, 5).map((c, i) => (
                      <img
                        key={c.login}
                        src={c.avatarUrl}
                        alt={c.login}
                        title={c.login}
                        className="h-6 w-6 rounded-full ring-2 ring-black"
                        style={{ marginLeft: i === 0 ? 0 : '-8px', zIndex: i }}
                      />
                    ))}
                  </div>
                ) : null}
                {/* Branch or last-commit date */}
                {(() => {
                  const git = status.data?.localGit;
                  if (!git?.exists) return <div className="flex-1" />;
                  const branch = git.branch && git.branch !== 'main' ? git.branch : null;
                  const label =
                    branch ?? (git.lastCommitDate ? formatTimeAgo(git.lastCommitDate) : null);
                  if (!label) return <div className="flex-1" />;
                  return (
                    <span
                      className="min-w-0 flex-1 truncate text-right text-[11px] tabular-nums"
                      style={{ color: 'var(--text-3)' }}
                      title={
                        branch ? `Branch: ${git.branch}` : `Last commit: ${git.lastCommitDate}`
                      }
                    >
                      {branch ? `⎇ ${label}` : label}
                    </span>
                  );
                })()}
              </div>
              {game.clonedFrom && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                    }
                  }}
                  className="flex items-center gap-1 text-[11px] text-white/35 transition-colors hover:text-white/65"
                  title={`Open ${game.clonedFrom} on GitHub`}
                  aria-label={`Open GitHub repo for ${game.displayName}`}
                >
                  <ExternalLink size={11} strokeWidth={1.8} />
                  <span>Open GitHub repo</span>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Legacy UI — main's card info layout, preserved verbatim. */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex h-2 w-2 shrink-0 rounded-full ${STATUS_COLOURS[statusBadge].split(' ')[0]}`}
                />
                <h3 className="truncate text-sm font-medium text-white/90">{game.displayName}</h3>
                {game.imported && (
                  <span className="shrink-0 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300/80">
                    Imported
                  </span>
                )}
                {game.clonedFrom && (
                  <span
                    className="shrink-0 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300/80"
                    title={`Cloned from ${game.clonedFrom}`}
                  >
                    Local copy
                  </span>
                )}
              </div>
              {game.clonedFrom && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                    }
                  }}
                  className="flex items-center gap-1 text-[11px] text-white/35 transition-colors hover:text-white/65"
                  title={`Open ${game.clonedFrom} on GitHub`}
                  aria-label={`Open GitHub repo for ${game.displayName}`}
                >
                  <ExternalLink size={11} strokeWidth={1.8} />
                  <span>Open GitHub repo</span>
                </div>
              )}
              {/* TemplateUpdateBadge + InterviewInProgressBadge are nested
                  role="button" descendants inside the card's role="button"
                  ancestor — same shape the legacy `<GameCard>` ships
                  (#462 docblock). Each badge `stopPropagation`s its own
                  click so the card doesn't open behind it; keyboard a11y
                  survives the same way. Pulling them out as siblings (a la
                  `<InstallStateChip>`) would require five absolutely-
                  positioned wrappers in this corner of the card and isn't
                  warranted while legacy carries the same nesting. */}
              <div className="flex items-center gap-1.5">
                {!game.imported && (
                  <TemplateUpdateBadge
                    gameId={game.gameId}
                    enabled={isVisible}
                    onClick={() => setShowUpdateModal(true)}
                  />
                )}
                <InterviewInProgressBadge
                  gameId={game.gameId}
                  enabled={isVisible}
                  onClick={onClick}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {/* Render the update modal outside the card surface so its
          inner buttons aren't nested inside the card's interactive
          ancestor. The modal is fixed-positioned so its JSX location
          doesn't affect visual placement. Mirrors the legacy
          `<GameCard>` pattern (line 853 in the unmerged tree). */}
      <UpdateTemplateModal
        gameId={game.gameId}
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onOpenWorkspace={() => navigate(`/workspace/${game.gameId}`)}
      />
    </>
  );
}

export function GameCard({
  game,
  onClick,
  newUi = false,
  favorite = false,
  onFavorite,
  onArchive,
}: {
  game: GameSummary;
  onClick: () => void;
  newUi?: boolean;
  favorite?: boolean;
  onFavorite?: (favorite: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const canOpenInEditor =
    capabilities.hasLocalFilesystem && canInvokeChannel(capabilities, 'game:open-in-editor');
  const canGenerateHero = canInvokeChannel(capabilities, 'game:generate-hero');
  // Issue #613: when the Update modal hits CRUCIBLE-312 (pending
  // conflict resolution) the user needs a one-click route to the
  // game's Workspace so they land on the existing
  // <ConflictResolutionCard>. Reusing the same `/workspace/:gameId`
  // route the card's outer `onClick` resolves to, instead of
  // plumbing the click handler down — the modal needs to call this
  // from a state-transition path, not a click event.
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [showStatus, setShowStatus] = useState(false);
  const [gameStatus, setGameStatus] = useState<GameStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  // #603 — track <img> load failure so we can swap to <GenreFallback>
  // when the URL the main process resolved is no longer on disk (e.g.
  // the game's local hero image was deleted between game:list and
  // render). Reset whenever heroImage changes so a fresh URL gets a
  // fresh try; this matches the same reset shape used by
  // <CommunityGameCard> below for consistency.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [game.heroImage]);

  const handleOpenInCursor = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!canOpenInEditor) return;
    window.crucible.invoke('game:open-in-editor', game.gameId);
  };

  const handleGenerateHero = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (generating || !canGenerateHero) return;
    setGenerating(true);
    setGenError(null);
    try {
      await window.crucible.invoke('game:generate-hero', {
        gameId: game.gameId,
        gameName: game.name,
      });
      invalidateGameLists(queryClient);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setGenError(msg);
      setTimeout(() => setGenError(null), 5000);
    } finally {
      setGenerating(false);
    }
  };

  const handleFavorite = (e: React.MouseEvent): void => {
    e.stopPropagation();
    void onFavorite?.(!favorite);
  };

  const handleArchive = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (
      confirm(
        `Archive "${game.name}"? It will be hidden from the Dashboard until you restore it. Files stay on disk.`,
      )
    ) {
      void onArchive?.();
    }
  };

  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (
      confirm(
        `Delete "${game.name}" locally? This removes the local files but keeps the GitHub repo.`,
      )
    ) {
      window.crucible.invoke('game:delete', { gameId: game.gameId }).then(
        () => invalidateGameLists(queryClient),

        (err) => console.warn('[game:delete] failed:', err),
      );
    }
  };

  // #462 — outer card is a div-role=button (not a native <button>) so
  // the inner action affordances (info, delete, open-in-cursor,
  // generate-art, badges) can stay as themselves without nesting
  // interactive role="button" inside an interactive ancestor.
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Only react when the event targets the card surface itself,
      // not when it bubbles up from a focused inner button/anchor.
      // Inner buttons already `stopPropagation()` on click, but
      // some keyboard events arrive here (Space on a focused inner
      // role="button" div, etc.). Guard on currentTarget so we
      // don't double-trigger.
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      onClick();
    }
  };

  return (
    <>
      {/* Codex YELLOW fold (#447 / #457) ∩ #462 hardening: the
        `<InstallStateChip variant="pill">` used to live INSIDE the outer
        GameCard surface, next to the game name. That nested an
        interactive element inside the card's interactive ancestor —
        invalid HTML (when the outer was `<button>`) and still an ARIA
        nested-role violation now that #462 promoted the outer to a
        `<div role="button">`. Either way the chip needs to escape the
        interactive ancestor.

        The chip now renders as a SIBLING of the outer card surface,
        positioned absolutely against the parent grid wrapper
        (Dashboard's grid item provides `relative` + `display: grid`,
        see line 253). The fragment here doesn't add a DOM node, so
        both the absolute-positioned chip and the auto-stretching card
        div remain direct children of the grid wrapper. Same visual
        prominence (corner badge); no nested interactive descendants;
        card-click still navigates because the chip's click handler
        calls `stopPropagation`. Position is top-right below the
        action toolbar so it doesn't visually overlap the archive/delete
        affordance. Mirrors the same out-of-button trick the
        comment further down uses for `<UpdateTemplateModal>`. */}
      <div className="pointer-events-auto absolute right-2 top-28 z-10">
        <InstallStateChip gameId={game.gameId} variant="pill" />
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleCardKeyDown}
        data-testid="gamecard-surface"
        aria-label={`Open ${game.name}`}
        className={
          newUi
            ? 'card-gradient group relative flex flex-col overflow-hidden rounded-2xl transition-all cursor-pointer hover:brightness-[1.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent'
            : 'group relative flex flex-col overflow-hidden rounded-lg border border-white/10 bg-crucible-surface transition-colors hover:border-white/20 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent'
        }
        style={
          newUi
            ? {
                boxShadow: 'var(--hairline), var(--shadow-card)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }
            : undefined
        }
      >
        {/* Hero image / placeholder.
          #603: fall back to <GenreFallback> when (a) the main process
          couldn't resolve any hero image (cloned game with no committed
          art, no bundled tile, no bundled genre WebP), or (b) the
          resolved URL was set but the load failed. The genre key is
          inferred from `name` + `description` so the placeholder
          matches the game's vibe instead of the previous one-cube-fits-
          all default-prototype.webp. */}
        <div className="relative aspect-video w-full bg-white/5">
          {game.heroImage && !imgFailed ? (
            <img
              src={game.heroImage}
              alt={game.name}
              className="h-full w-full object-cover"
              // Dashboard lazy-load plan §"Hero image hydrate": let the
              // browser defer off-viewport image fetches + decode them
              // off the main thread. Free win on 110-card grids.
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <GenreFallback
              genreKey={inferGenreKey(game.name, game.description)}
              alt={game.name}
              className="h-full w-full object-cover"
            />
          )}
          {generating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              <span className="text-xs font-medium text-white/70">Generating artwork...</span>
            </div>
          )}
          {genError && (
            <div className="absolute inset-x-0 bottom-0 bg-red-900/90 px-3 py-2 text-xs text-red-200 backdrop-blur-sm">
              {genError}
            </div>
          )}
        </div>

        {onFavorite && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleFavorite}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleFavorite(e as unknown as React.MouseEvent);
              }
            }}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={favorite ? `Unfavorite ${game.name}` : `Favorite ${game.name}`}
            className={`${newUi ? 'left-2 top-2' : 'left-2 top-2'} absolute flex h-7 w-7 items-center justify-center rounded-md bg-black/60 ${favorite ? 'text-[#FFDA0A]' : 'text-white/50'} opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-[#FFDA0A] group-hover:opacity-100`}
          >
            <Star size={14} strokeWidth={1.8} fill={favorite ? 'currentColor' : 'none'} />
          </div>
        )}

        {/* New UI: generate-artwork button at right-2 top-11 (DOM-first
          so it's first in tab order, #462). */}
        {newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.name}`
                : `Generate artwork for ${game.name}`
            }
            className="absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}
        {/* Legacy UI: main's generate-artwork button at left-2 top-2. */}
        {!newUi && !game.imported && canGenerateHero && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.name}`
                : `Generate artwork for ${game.name}`
            }
            className={`absolute ${onFavorite ? 'left-2 top-11' : 'left-2 top-2'} flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100`}
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}

        {/* Open in Cursor button (top-right corner) */}
        {canOpenInEditor && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleOpenInCursor}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleOpenInCursor(e as unknown as React.MouseEvent);
              }
            }}
            title="Open in Cursor"
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 2L2 8l3 6M11 2l3 6-3 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M8 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        )}

        {/* Delete button (#462 — a11y: tabIndex=0, stopPropagation so the
          card-open doesn't fire). Position differs between UIs: new UI
          places it at right-2 top-20 (separate from generate-hero at
          top-11); legacy UI places it at right-2 top-11 (no generate
          button on the right column there). */}
        {newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={onArchive ? handleArchive : handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                (onArchive ? handleArchive : handleDelete)(e as unknown as React.MouseEvent);
              }
            }}
            title={onArchive ? 'Archive game' : 'Delete locally'}
            aria-label={onArchive ? `Archive ${game.name}` : `Delete ${game.name} locally`}
            className="absolute right-2 top-20 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          >
            {onArchive ? (
              <Archive size={14} strokeWidth={1.8} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg"
              >
                <polyline points="3,4 13,4" />
                <path d="M6,4V2h4v2" />
                <path d="M5,4l1,10h4l1-10" />
              </svg>
            )}
          </div>
        )}
        {!newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={onArchive ? handleArchive : handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                (onArchive ? handleArchive : handleDelete)(e as unknown as React.MouseEvent);
              }
            }}
            title={
              onArchive ? 'Archive game' : game.imported ? 'Remove from Foundry' : 'Delete locally'
            }
            aria-label={
              onArchive
                ? `Archive ${game.name}`
                : game.imported
                  ? `Remove ${game.name} from Foundry`
                  : `Delete ${game.name} locally`
            }
            className={`absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity ${onArchive ? 'hover:bg-black/80 hover:text-white/90' : 'hover:bg-red-900/80 hover:text-red-300'} group-hover:opacity-100`}
          >
            {onArchive ? (
              <Archive size={14} strokeWidth={1.8} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        )}

        {/* Info */}
        <div className="flex flex-1 flex-col gap-1 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3
                className={`truncate font-medium group-hover:text-white${newUi ? '' : ' text-white/90'}`}
                style={newUi ? { color: 'var(--text-1)' } : undefined}
              >
                {game.name}
              </h3>
              {game.category && (
                <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400/80">
                  {game.category}
                </span>
              )}
              {newUi ? (
                <UpdateIconButton gameId={game.gameId} onClick={() => setShowUpdateModal(true)} />
              ) : (
                <>
                  {game.imported && (
                    <span className="shrink-0 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300/80">
                      Imported
                    </span>
                  )}
                  {game.clonedFrom && (
                    <span
                      className="shrink-0 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300/80"
                      title={`Cloned from ${game.clonedFrom}`}
                    >
                      Local copy
                    </span>
                  )}
                  {!game.imported && (
                    <TemplateUpdateBadge
                      gameId={game.gameId}
                      onClick={() => setShowUpdateModal(true)}
                    />
                  )}
                  <InterviewInProgressBadge gameId={game.gameId} onClick={onClick} />
                  {/* Codex YELLOW fold (#447 / #457): `<InstallStateChip
                    variant="pill">` used to render HERE — inline next to the
                    game name. That nested the chip's `<button>` inside the
                    outer GameCard `<button>` (invalid HTML, real a11y
                    regression). Moved to the GameCard's relative wrapper as a
                    sibling-overlay positioned absolutely. See the wrapper at
                    the top of this function for the placement. */}
                </>
              )}
            </div>
            {game.clonedFrom && (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                  }
                }}
                className="flex w-fit items-center gap-1 text-[11px] text-white/35 transition-colors hover:text-white/65"
                title={`Open ${game.clonedFrom} on GitHub`}
                aria-label={`Open GitHub repo for ${game.name}`}
              >
                <ExternalLink size={11} strokeWidth={1.8} />
                <span>Open GitHub repo</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!showStatus && !gameStatus && !statusLoading) {
                    setStatusLoading(true);
                    window.crucible
                      .invoke('game:status', game.gameId)
                      .then((s) => {
                        setGameStatus(s);
                        setStatusLoading(false);
                      })
                      .catch(() => setStatusLoading(false));
                  }
                  setShowStatus(!showStatus);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    if (!showStatus && !gameStatus && !statusLoading) {
                      setStatusLoading(true);
                      window.crucible
                        .invoke('game:status', game.gameId)
                        .then((s) => {
                          setGameStatus(s);
                          setStatusLoading(false);
                        })
                        .catch(() => setStatusLoading(false));
                    }
                    setShowStatus(!showStatus);
                  }
                }}
                title="View status"
                className="flex h-5 w-5 items-center justify-center rounded text-white/30 transition-colors hover:bg-white/10 hover:text-white/60"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
                  <path
                    d="M8 7v4M8 5.5v.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOURS[game.status]}`}
              >
                {game.status}
              </span>
            </div>
          </div>
          <p className="text-xs text-white/40">{formatTimeAgo(game.lastModified)}</p>
        </div>

        {/* Status detail panel */}
        {showStatus && (
          <div className="border-t border-white/10 p-3" onClick={(e) => e.stopPropagation()}>
            {statusLoading && <p className="text-xs text-white/30">Loading status...</p>}
            {gameStatus && <GameStatusPanel status={gameStatus} />}
            {!statusLoading && !gameStatus && (
              <p className="text-xs text-white/30">Could not load status</p>
            )}
          </div>
        )}
      </div>
      {/* Render the update modal outside the card surface so its inner
        <button>s aren't nested inside an interactive ancestor. The
        modal is fixed-positioned so the JSX location doesn't affect
        visual placement. (Comment preserved from when the outer card
        was a <button>; #462 swapped that for div role="button" but
        the modal-outside-card invariant still matters for the modal's
        own interactive children.) */}
      <UpdateTemplateModal
        gameId={game.gameId}
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        // Issue #613: route the user to the game's Workspace on
        // CRUCIBLE-312 (pending conflict resolution) so they land on
        // the existing `<ConflictResolutionCard>`. The modal closes
        // itself before invoking this callback so the navigate
        // doesn't race the modal's exit animation.
        onOpenWorkspace={() => navigate(`/workspace/${game.gameId}`)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// New Game Choice Dialog
// ---------------------------------------------------------------------------

export function NewGameChoiceDialog({
  onClose,
  onCreate,
  onImport,
  canImportLocalProject = true,
}: {
  onClose: () => void;
  onCreate: () => void;
  onImport: () => void;
  canImportLocalProject?: boolean;
}): React.JSX.Element {
  return (
    <div className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Add Game</h2>
            <p className="mt-1 text-sm text-white/50">
              {canImportLocalProject
                ? 'Start from a prompt, or bring an existing project into Foundry.'
                : 'Start from a prompt and build in Cloud Foundry.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 px-3 py-1.5 text-sm text-white/50 transition-colors hover:border-white/20 hover:text-white/80"
          >
            Close
          </button>
        </div>

        <div className="grid gap-3">
          <button
            onClick={onCreate}
            className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/15 p-4 text-left transition-colors hover:border-crucible-accent/70 hover:bg-crucible-accent/20"
          >
            <span className="block text-sm font-semibold text-white">Create from prompt</span>
            <span className="mt-1 block text-sm text-white/55">
              Scaffold a new game and start the AI interview flow.
            </span>
          </button>

          {canImportLocalProject && (
            <button
              onClick={onImport}
              className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:border-white/25 hover:bg-white/[0.06]"
            >
              <span className="block text-sm font-semibold text-white">
                Import existing project
              </span>
              <span className="mt-1 block text-sm text-white/55">
                Register a local Crucible, hello-weekend, or VGF project without copying it.
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import Game Dialog
// ---------------------------------------------------------------------------

type ImportStage = 'picking' | 'confirming' | 'importing' | 'success' | 'error';

export function ImportGameDialog({
  onClose,
  onImported,
  canImportLocalProject = true,
}: {
  onClose: () => void;
  onImported: (gameId: string) => void;
  canImportLocalProject?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<ImportStage>('picking');
  const [preview, setPreview] = useState<ImportGamePreview | null>(null);
  const [gameId, setGameId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errorState, setErrorState] = useState<SerialisedError | null>(null);
  const [navigateAfterImportId, setNavigateAfterImportId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (stage !== 'picking') return;
    if (!canImportLocalProject) return;
    window.crucible.invoke('game:import:pick-directory').then(
      (result) => {
        if (cancelled) return;
        if (!result) {
          onClose();
          return;
        }
        setPreview(result);
        setGameId(result.gameId);
        setDisplayName(result.displayName);
        setStage('confirming');
      },
      (err) => {
        if (cancelled) return;
        setErrorState(unflattenIpcError(err));
        setStage('error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [stage, onClose, canImportLocalProject]);

  // Defer navigation so the success state is visible; clear on unmount
  // so closing the dialog before the delay does not call onImported.
  useEffect(() => {
    if (stage !== 'success' || !navigateAfterImportId) return;
    const timer = setTimeout(() => onImported(navigateAfterImportId), 900);
    return () => clearTimeout(timer);
  }, [stage, navigateAfterImportId, onImported]);

  const handleImport = async (): Promise<void> => {
    if (!canImportLocalProject) return;
    if (!preview || !preview.canImport || !gameId.trim() || !displayName.trim()) return;
    setStage('importing');
    setErrorState(null);
    try {
      const result = await window.crucible.invoke('game:import:commit', {
        selectedPath: preview.selectedPath,
        gameId: gameId.trim(),
        displayName: displayName.trim(),
      });
      invalidateGameLists(queryClient);
      setNavigateAfterImportId(result.gameId);
      setStage('success');
    } catch (err) {
      setErrorState(unflattenIpcError(err));
      setStage('error');
    }
  };

  return (
    <div className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        {!canImportLocalProject && (
          <div className="flex flex-col gap-4 py-4">
            <h2 className="text-lg font-semibold">Local import unavailable</h2>
            <p className="text-sm text-white/55">
              Local project import is available in Local Foundry.
            </p>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {canImportLocalProject && stage === 'picking' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
            <p className="text-sm text-white/60">Opening folder picker...</p>
          </div>
        )}

        {canImportLocalProject && stage === 'confirming' && preview && (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-lg font-semibold">Import Existing Game</h2>
              <p className="mt-1 break-all text-xs text-white/40">{preview.selectedPath}</p>
            </div>

            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/60">Detected</span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/70">
                  {preview.kind}
                </span>
              </div>
              {!preview.canImport && preview.unsupportedReason && (
                <p className="mt-2 text-sm text-red-300">{preview.unsupportedReason}</p>
              )}
            </div>

            <label className="block text-sm text-white/60">
              Display Name
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-crucible-accent"
              />
            </label>

            <label className="block text-sm text-white/60">
              Game ID
              <input
                type="text"
                value={gameId}
                onChange={(e) => setGameId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 font-mono text-sm text-white outline-none focus:border-crucible-accent"
              />
            </label>

            {preview.warnings.length > 0 && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                {preview.warnings.map((warning) => (
                  <p key={warning} className="text-xs text-amber-100/80">
                    {warning}
                  </p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!preview.canImport || !gameId.trim() || !displayName.trim()}
                className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Import
              </button>
            </div>
          </div>
        )}

        {canImportLocalProject && stage === 'importing' && (
          <LoadingOverlay message="Importing game..." isVisible />
        )}

        {canImportLocalProject && stage === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#22c55e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">Game Imported</h2>
          </div>
        )}

        {canImportLocalProject && stage === 'error' && errorState && (
          <div className="flex flex-col gap-4 py-4">
            <h2 className="text-lg font-semibold">Import Failed</h2>
            <CrucibleErrorState
              error={errorState}
              onRetry={() => {
                setErrorState(null);
                setStage('picking');
              }}
            />
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Game Dialog
// ---------------------------------------------------------------------------

type CreateStage = 'form' | 'creating' | 'success' | 'error';

export function CreateGameDialog({
  onClose,
  onCreated,
  initialStage: _initialStage,
  initialGameName: _initialGameName,
  initialProgressMessages: _initialProgressMessages,
}: {
  onClose: () => void;
  onCreated: (gameId: string, routeState?: CreateHandoffRouteState) => void;
  /** Storybook/test only — seed the initial stage. */
  initialStage?: CreateStage;
  /** Storybook/test only — seed the game name field. */
  initialGameName?: string;
  /** Storybook/test only — seed progress messages. */
  initialProgressMessages?: string[];
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const { newUi, designTab } = useFeatureFlags();
  const [gameName, setGameName] = useState(_initialGameName ?? '');
  const [description, setDescription] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');
  // #191 quick-batch — Quick is the default on every open. Per the spec,
  // we do NOT remember the user's last choice across dialog opens.
  // #1773 — the kickoff control also offers 'just-build' (zero
  // questions), a renderer-only mode mapped to quick depth +
  // `interviewBestEffort: true` at the game:create call below.
  const [interviewDepth, setInterviewDepth] = useState<CreateGameKickoffMode>('quick');
  const [stage, setStage] = useState<CreateStage>(_initialStage ?? 'form');
  const [progressMessages, setProgressMessages] = useState<string[]>(
    _initialProgressMessages ?? [],
  );
  // Tier 3: structured error so the failure card can render the
  // CrucibleError recovery + code (game:create wraps GitHub / template
  // failures with rich CrucibleError instances).
  const [errorState, setErrorState] = useState<SerialisedError | null>(null);
  // Issue #441 — non-fatal warning surfaced by the scaffold-rollback
  // path when it can't delete the orphan GitHub repo (the user's
  // token is missing `delete_repo` scope). The `recovery` field
  // carries a copy-pasteable shell snippet the user can run to clean
  // up manually. Kept separate from `errorState` because the create
  // flow ALSO surfaces a normal CrucibleError as the primary failure
  // — the warning is a secondary "and also, this orphan repo is on
  // GitHub" notice that needs its own affordance.
  const [rollbackWarning, setRollbackWarning] = useState<{
    message: string;
    recovery?: string;
  } | null>(null);

  useEffect(() => {
    setInterviewDepth((current) => {
      if (current === 'full') return 'quick';
      if (current === 'advanced' && !designTab) return 'quick';
      return current;
    });
  }, [designTab]);
  const [createdGameId, setCreatedGameId] = useState('');

  // Subscribe to progress events while creating
  useEffect(() => {
    if (stage !== 'creating') return;

    const unsub = window.crucible.on('game:create:progress', (event) => {
      if (event.message) {
        setProgressMessages((prev) => [...prev, event.message]);
      }
      if (event.warning) {
        // #441 — orphan-repo cleanup hint. Don't flip to 'error'
        // stage on its own — the warning is non-fatal; the real
        // failure event will arrive as `event.error` separately.
        setRollbackWarning({
          message: event.warning,
          ...(event.recovery ? { recovery: event.recovery } : {}),
        });
      }
      if (event.error) {
        // game:create:progress carries plain strings; wrap into a
        // SerialisedError so the renderer always handles one shape.
        setErrorState(unflattenIpcError(event.error));
        setStage('error');
      }
    });

    return unsub;
  }, [stage]);

  const handleCreate = useCallback(async () => {
    // Slugify the user-typed gameName UNCONDITIONALLY so the kebab path is
    // identical regardless of UI flavour (codex r1 #1213 MAJOR — previously
    // the slugify call lived inside a `newUi ? ... : ...` ternary which made
    // it ambiguous which branch ran in which UI; co-locating it here removes
    // the doubt + leaves the conditional purely about which value to USE).
    // The slugifier mirrors the CLI's `toKebabCase()` from
    // packages/crucible/src/template/tokens.ts (#1213 reproducer:
    // "Alien Breed II" → "alien-breed-ii"). Inlined as a separate util in
    // `./slugify.ts` because the CLI's helper lives in a Node-side package
    // that crucible-ui must not depend on directly (browser-safe constraint
    // from BG-1 lift).
    const slugifiedGameName = slugifyToKebab(gameName);
    const conceptText = initialPrompt.trim();
    const descriptionText = normalizeCreateDescription(description);
    const descriptionTooLong =
      !newUi && (descriptionText?.length ?? 0) > GAME_DESCRIPTION_MAX_LENGTH;
    if (newUi && !conceptText) return;
    if (!newUi && !gameName.trim()) return;
    if (descriptionTooLong) return;

    setStage('creating');
    setProgressMessages([]);
    setErrorState(null);
    // #441 — clear any stale orphan-repo warning from a prior
    // attempt so a successful retry doesn't show the old hint.
    setRollbackWarning(null);

    try {
      // Pre-flight: verify the GitHub PAT has the scopes Crucible
      // needs BEFORE we kick off the minute-long create flow. The
      // common mode here is a token granted only `repo` — every push
      // that touches `.github/workflows/*` (which is every Crucible
      // game scaffold) gets server-side rejected once we get to the
      // git-push step. Catching this up front saves the user the wait
      // and the "delete dangling repo" cleanup. Codex follow-up
      // 2026-05-01.
      //
      // Behaviour is **fail-open on unrelated failures**: we ONLY
      // block on `status === 'invalid' && missingScopes.length > 0`,
      // which is the specific scope-deficit signal. A network blip
      // during the verify (`reason: 'network'`), a 500 from GitHub,
      // a fine-grained PAT (no scope check applies), or any other
      // verify failure that isn't about scope all fall through to
      // `game:create`. That handler already surfaces the underlying
      // problem with structured copy of its own — blocking here
      // would just double-up error UX without telling the user
      // anything new. We share `CRUCIBLE-207` with the reactive
      // catch in `core/create.ts` so support docs can reference one
      // code regardless of which side surfaced the issue first.
      const verify = await window.crucible.invoke('tokens:verify');

      // Pre-flight: block create when any system prereq is missing
      // OR broken at runtime (git / Node / pnpm). Without this gate
      // the user clicks Create, the GUI scaffolds the game directory,
      // hits `pnpm install` mid-flow, and surfaces a CRUCIBLE-806 —
      // confusing because they don't know which tool is the problem
      // until they read the error.
      //
      // Issue #308: distinguish "missing from PATH" from "installed
      // but crashes at startup". The CEO hit the latter on
      // 2026-05-07 — Homebrew pnpm 11.0.8 was on PATH but exited
      // immediately with `node:sqlite ERR_UNKNOWN_BUILTIN_MODULE`
      // because the formula linked it against a Node binary that
      // doesn't have the built-in. Telling that user to "install
      // pnpm" was actively wrong; the fix is to swap the broken
      // build for a working one. The full classification + copy
      // logic lives in `prereq-error.ts` so it's testable in
      // isolation; here we just surface the result.
      const prereqErr = buildPrereqError(verify);
      if (prereqErr) {
        setErrorState(prereqErr);
        setStage('error');
        return;
      }

      const ghMissing = verify.github.missingScopes ?? [];
      if (verify.github.status === 'invalid' && ghMissing.length > 0) {
        setErrorState({
          name: 'CrucibleError',
          message: verify.github.error ?? 'Your GitHub token is missing required scopes.',
          category: 'auth',
          retryable: false,
          code: 'CRUCIBLE-207',
          recovery:
            'Open Settings → Connections → GitHub, paste a token with the missing scopes ticked, then try Create again.',
        });
        setStage('error');
        return;
      }

      // Decide what to send as `displayName`. Two cases:
      //   - New UI: ask the AI for a creative name from the concept text.
      //     Display name is whatever the AI suggests, falling back to the
      //     slug if the suggest IPC fails.
      //   - Legacy UI: preserve the user-typed `gameName` (with its
      //     original casing + spaces + punctuation) as the display name.
      //     `nameToUse` is the slugified form for paths/repos; the user
      //     will see their original text in Foundry's game list.
      //
      // Codex r2 #1213 MAJOR fix: previously this branch only assigned
      // `displayNameToUse` in the `newUi` path. In legacy UI, the
      // `game:create` IPC payload omitted `displayName` entirely, and the
      // CLI fell back to `options.name` (the slug) — so a user typing
      // "Alien Breed II" saw their game named "alien-breed-ii" in the
      // dashboard. Preserving the trimmed original here keeps the slug
      // legal AND the display name human.
      let displayNameToUse: string | undefined;
      if (newUi) {
        try {
          const suggested = await window.crucible.invoke('game:suggest-name', { conceptText });
          displayNameToUse = suggested.displayName;
        } catch {
          // Non-fatal — slug serves as display name
        }
      } else {
        displayNameToUse = gameName.trim() || undefined;
      }

      // #1824 — derive the on-disk / repo slug.
      //   - New UI: FROM the concept the user typed, so the project folder +
      //     repo reflect what the game is (e.g. "a peggle-style ball bouncer"
      //     → "peggle-style-ball-bouncer") instead of a random codename. A
      //     numeric `-2`/`-3` suffix is added ONLY on collision with an
      //     existing local game id; the common case is a clean, suffix-free
      //     name. Best-effort collision check: fetch the local game list and
      //     fall back to an empty set (core CRUCIBLE-201 pre-flight + recovery
      //     backstops a race) if the list isn't available on this transport.
      //   - Legacy UI: the slugified user-typed name (unchanged).
      let nameToUse: string;
      if (newUi) {
        let existingGameIds: string[] = [];
        try {
          const games = await window.crucible.invoke('game:list:lite');
          existingGameIds = games.map((g) => g.gameId);
        } catch {
          /* non-fatal — collision check is best-effort; core backstops races */
        }
        nameToUse = deriveCreateNameFromConcept(conceptText, existingGameIds);
        // Mirror the slug into the (hidden, new-UI) game-name field so a
        // subsequent retry from the same dialog reuses it — preserves the
        // pre-#1824 `setGameName` behaviour.
        setGameName(nameToUse);
      } else {
        nameToUse = slugifiedGameName;
      }

      // #1773 — 'just-build' is a renderer-only kickoff mode: the wire
      // carries the persisted depth ('quick') plus the best-effort flag
      // the create core stamps onto the interview seed.
      const justBuild = interviewDepth === 'just-build';
      const wireInterviewDepth = interviewDepth === 'just-build' ? 'quick' : interviewDepth;
      const autoKickoff = wireInterviewDepth !== 'advanced';
      const result = await window.crucible.invoke('game:create', {
        name: nameToUse,
        ...(displayNameToUse ? { displayName: displayNameToUse } : {}),
        description: newUi ? deriveCreateDescription(conceptText) : descriptionText,
        initialPrompt: newUi ? conceptText || undefined : initialPrompt.trim() || undefined,
        // #191 quick-batch — thread the toggle pick through so the
        // create core can seed `.foundry/build-game-interview.json`
        // with `interview.depth = <value>` before the initial commit.
        interviewDepth: wireInterviewDepth,
        ...(justBuild ? { interviewBestEffort: true } : {}),
      });

      setCreatedGameId(result.gameId);
      setStage('success');

      // Invalidate the game list cache so it refreshes
      invalidateGameLists(queryClient);

      // Navigate to workspace after a short delay
      const routeState = buildCreateHandoffRouteState({
        prompt: newUi ? conceptText : initialPrompt,
        autoKickoff,
      });
      setTimeout(() => onCreated(result.gameId, routeState), 1200);
    } catch (err) {
      setErrorState(unflattenIpcError(err));
      setStage('error');
    }
  }, [newUi, gameName, description, initialPrompt, interviewDepth, onCreated, queryClient]);

  const legacyDescriptionText = normalizeCreateDescription(description);
  const legacyDescriptionLength = legacyDescriptionText?.length ?? 0;
  const legacyDescriptionTooLong = legacyDescriptionLength > GAME_DESCRIPTION_MAX_LENGTH;

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && stage === 'form') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, stage]);

  return (
    // `app-region-no-drag` — the shared fullscreen shell header is the
    // Electron drag surface, so a `fixed inset-0` overlay needs the explicit
    // opt-out or its backdrop strip near the top of the window is interpreted
    // as a window-drag by the macOS compositor.
    <div className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className={`w-full rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl ${newUi ? 'max-w-xl' : 'max-w-md'}`}
      >
        {/* Form stage — new UI: single concept textarea */}
        {stage === 'form' && newUi && (
          <>
            <p className="mb-3 text-[13px] text-white/40">What would you like to build?</p>
            <textarea
              value={initialPrompt}
              onChange={(e) => setInitialPrompt(e.target.value)}
              placeholder="Describe the game you want to build — genre, mechanics, vibe, anything..."
              autoFocus
              rows={5}
              className="mb-4 w-full resize-none rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-[15px] leading-relaxed text-white placeholder-white/20 outline-none focus:border-white/20"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && initialPrompt.trim())
                  handleCreate();
              }}
            />
            <div className="flex items-center justify-between">
              <div
                role="radiogroup"
                aria-label="Kickoff mode"
                className="inline-flex rounded-lg border border-white/10 bg-white/5 p-1 text-[13px]"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={interviewDepth === 'quick'}
                  onClick={() => setInterviewDepth('quick')}
                  className={`rounded-md px-3 py-1 transition-colors ${interviewDepth === 'quick' ? 'bg-crucible-accent text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`}
                >
                  Fast
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={interviewDepth === 'just-build'}
                  onClick={() => setInterviewDepth('just-build')}
                  title="No questions — the agent makes sensible choices and starts building immediately"
                  className={`rounded-md px-3 py-1 transition-colors ${interviewDepth === 'just-build' ? 'bg-crucible-accent text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`}
                >
                  Just build
                </button>
                {designTab && (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={interviewDepth === 'advanced'}
                    onClick={() => setInterviewDepth('advanced')}
                    title="Author a full design doc in the Design tab — replaces the interview"
                    className={`rounded-md px-3 py-1 transition-colors ${interviewDepth === 'advanced' ? 'bg-crucible-accent text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`}
                  >
                    Advanced
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg px-3 py-2 text-[13px] text-white/40 transition-colors hover:text-white/70"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!initialPrompt.trim()}
                  className="glint-halo rounded-lg bg-crucible-accent px-4 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Create
                </button>
              </div>
            </div>
          </>
        )}

        {/* Form stage — legacy UI */}
        {stage === 'form' && !newUi && (
          <>
            <h2 className="mb-4 text-lg font-semibold">Create New Game</h2>

            <label className="mb-1 block text-sm text-white/60">
              Game Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              placeholder="e.g. Space Invaders"
              autoFocus
              className="mb-4 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-crucible-accent"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && gameName.trim()) handleCreate();
              }}
            />

            <label className="mb-1 block text-sm text-white/60">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description of your game"
              className="mb-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-crucible-accent"
            />
            <div className="mb-4 flex items-center justify-between gap-3 text-xs">
              {legacyDescriptionTooLong ? (
                <p className="text-red-300">Description must be 500 characters or fewer.</p>
              ) : (
                <span aria-hidden="true" />
              )}
              <span className={legacyDescriptionTooLong ? 'text-red-300' : 'text-white/35'}>
                {legacyDescriptionLength}/{GAME_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>

            <label className="mb-1 block text-sm text-white/60">Initial AI Prompt</label>
            <textarea
              value={initialPrompt}
              onChange={(e) => setInitialPrompt(e.target.value)}
              placeholder="Tell the AI what kind of game to build..."
              rows={3}
              className="mb-4 w-full resize-none rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-crucible-accent"
            />

            <CreateGameInterviewDepthToggle
              value={interviewDepth}
              onChange={setInterviewDepth}
              allowAdvanced={designTab}
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!gameName.trim() || legacyDescriptionTooLong}
                className="glint-halo rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Create
              </button>
            </div>
          </>
        )}

        {/* Creating stage — full-screen LoadingOverlay */}
        {stage === 'creating' && (
          <LoadingOverlay
            message={
              progressMessages.length > 0
                ? progressMessages[progressMessages.length - 1]
                : newUi
                  ? 'Creating your game...'
                  : `Creating ${gameName}...`
            }
            isVisible
          />
        )}

        {/* Success stage */}
        {stage === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#22c55e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">Game Created!</h2>
            <p className="text-sm text-white/50">Opening {createdGameId} workspace...</p>
          </div>
        )}

        {/* Error stage — Tier 3: structured CrucibleError card with
            recovery hint + Copy-for-support button. */}
        {stage === 'error' && errorState && (
          <div className="flex flex-col gap-4 py-4">
            <h2 className="text-lg font-semibold">Creation Failed</h2>
            <CrucibleErrorState
              error={errorState}
              onRetry={() => {
                setStage('form');
                setProgressMessages([]);
                setErrorState(null);
                setRollbackWarning(null);
              }}
            />
            {rollbackWarning && (
              <RollbackWarningCard
                message={rollbackWarning.message}
                recovery={rollbackWarning.recovery}
              />
            )}
            {progressMessages.length > 0 && (
              <div className="max-h-32 overflow-auto rounded-lg bg-black/30 p-3">
                {progressMessages.map((msg, i) => (
                  <p key={i} className="text-xs text-white/50">
                    {msg}
                  </p>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Community Games — all games across the org
// ---------------------------------------------------------------------------

function CommunityGames({
  localGameIds,
  showTestGames,
  discovered,
  isLoading,
  error,
  preferences,
  onArchive,
  onFavorite,
  newUi = false,
  searchQuery = '',
}: {
  localGameIds: string[];
  showTestGames: boolean;
  discovered?: DiscoveredGame[];
  isLoading: boolean;
  error: unknown;
  preferences: DashboardPreferences | undefined;
  onArchive: (input: DashboardArchiveSetInput) => Promise<void>;
  onFavorite: (input: DashboardFavoriteSetInput) => Promise<void>;
  newUi?: boolean;
  searchQuery?: string;
}): React.JSX.Element | null {
  const navigate = useNavigate();

  // When the user defers their GitHub PAT (Tier-2 first-run rework allows
  // this — the PAT moved out of onboarding), `game:discover` throws
  // "GitHub token not set..." at the IPC boundary. Codex flagged that the
  // previous implementation hid that failure entirely, so the Community
  // Games section just vanished with no explanation. Show an inline CTA
  // pointing the user at Settings instead — same affordance the failure
  // mode plan recommended for every "go to Settings" error path.
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    const isMissingToken = /GitHub token not set/i.test(message);
    if (!isMissingToken) {
      // Other discovery errors (network, 5xx) shouldn't masquerade as a
      // missing-token CTA — render a generic banner and let the user retry.
      return (
        <div className="mt-12 border-t border-white/10 pt-8">
          <h2 className="mb-2 text-lg font-medium text-white/70">Community Games</h2>
          <p className="text-sm text-white/40">
            Couldn&apos;t load community games right now. {message}
          </p>
        </div>
      );
    }
    return (
      <div className="mt-12 border-t border-white/10 pt-8">
        <h2 className="mb-2 text-lg font-medium text-white/70">Community Games</h2>
        <div className="rounded-lg border border-white/10 bg-white/5 p-4">
          <p className="text-sm text-white/60">
            Browse other team games once you connect a GitHub token.
          </p>
          <p className="mt-1 text-xs text-white/40">
            You can do this any time — it&apos;s only needed for browsing and publishing.
          </p>
          <button
            onClick={() => navigate('/settings')}
            className="mt-3 rounded-lg bg-crucible-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Open Settings
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !discovered)
    return (
      <div className="mt-12 min-h-[calc(100vh-12rem)] border-t border-white/[0.08] pt-8">
        <h2 className="mb-6 f-title" style={{ color: 'var(--text-2)' }}>
          Community Games
        </h2>
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
        </div>
      </div>
    );

  // Filter out local games, (optionally) test games, and apply name search
  const communityGames = filterDashboardCommunityGames(discovered, preferences, {
    localGameIds,
    showTestGames,
    searchQuery,
  }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  if (communityGames.length === 0) return null;

  return (
    <div className="mt-12 border-t border-white/[0.08] pt-8">
      <h2 className="mb-4 f-title" style={{ color: 'var(--text-2)' }}>
        Community Games
      </h2>
      {newUi ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {communityGames.map((game) => (
            <CommunityGameCard
              key={dashboardPreferenceKey({
                scope: 'community',
                gameId: game.gameId,
                repoFullName: game.repoFullName,
              })}
              game={game}
              favorite={isDashboardFavorited(preferences, {
                scope: 'community',
                gameId: game.gameId,
                repoFullName: game.repoFullName,
              })}
              onArchive={() => onArchive({ ...communityPreferencePayload(game), archived: true })}
              onFavorite={(favorite) =>
                onFavorite({ ...communityPreferencePayload(game), favorite })
              }
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {communityGames.map((game) => (
            <CommunityGameCard
              key={dashboardPreferenceKey({
                scope: 'community',
                gameId: game.gameId,
                repoFullName: game.repoFullName,
              })}
              game={game}
              favorite={isDashboardFavorited(preferences, {
                scope: 'community',
                gameId: game.gameId,
                repoFullName: game.repoFullName,
              })}
              onArchive={() => onArchive({ ...communityPreferencePayload(game), archived: true })}
              onFavorite={(favorite) =>
                onFavorite({ ...communityPreferencePayload(game), favorite })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityGameRow({
  game,
  isLast,
}: {
  game: DiscoveredGame;
  isLast?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const canCloneGame = canInvokeChannel(capabilities, 'game:clone');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [game.heroImage]);

  const handleClone = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (cloning || !canCloneGame) return;
    setCloning(true);
    setCloneError(null);
    try {
      await window.crucible.invoke('game:clone', {
        gameId: game.gameId,
        repoFullName: game.repoFullName,
      });
      invalidateGameLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['game:discover'] });
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Clone failed');
      setTimeout(() => setCloneError(null), 5000);
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="group relative">
      <div
        className="relative flex items-center gap-3 rounded-xl px-3 py-[14px] transition-colors group-hover:bg-[var(--surface-row-hover)]"
        style={{
          transitionDuration: 'var(--motion-fast)',
          transitionTimingFunction: 'var(--ease-flora)',
        }}
      >
        <div className="relative h-12 w-[84px] shrink-0 overflow-hidden rounded-lg bg-white/[0.04]">
          {game.heroImage && !imgFailed ? (
            <img
              src={game.heroImage}
              alt={game.displayName}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <GenreFallback
              genreKey={inferGenreKey(game.displayName, game.description)}
              alt={game.displayName}
              className="h-full w-full object-cover"
            />
          )}
        </div>

        {/* Name */}
        <p className="min-w-0 flex-1 truncate text-sm" style={{ color: 'var(--text-2)' }}>
          {game.displayName}
        </p>

        {/* Updated date */}
        <span className="shrink-0 text-xs tabular-nums" style={{ color: 'var(--text-3)' }}>
          {formatTimeAgo(game.updatedAt)}
        </span>

        {/* Contributor avatars — stacked, rightmost is top contributor */}
        {game.contributors && game.contributors.length > 0 && (
          <div className="flex shrink-0 items-center">
            {game.contributors.slice(0, 5).map((c, i) => (
              <img
                key={c.login}
                src={c.avatarUrl}
                alt={c.login}
                title={c.login}
                className="h-8 w-8 rounded-full ring-2 ring-black"
                style={{ marginLeft: i === 0 ? 0 : '-10px', zIndex: i }}
              />
            ))}
          </div>
        )}

        {/* Clone error */}
        {cloneError && <span className="shrink-0 text-xs text-red-400">{cloneError}</span>}

        {/* Action — outlined + download when remote, filled + harddrive when local */}
        {game.isLocal ? (
          <div
            className="shrink-0 rounded-full p-1.5"
            style={{ background: 'var(--border-alpha-1)', color: 'var(--text-2)' }}
            title="Cloned locally"
          >
            <Check size={16} strokeWidth={2} />
          </div>
        ) : canCloneGame ? (
          <button
            onClick={handleClone}
            disabled={cloning}
            title="Clone to edit"
            className="shrink-0 rounded-full border border-white/[0.11] p-1.5 transition-all hover:border-white/[0.18] hover:bg-white/[0.04] disabled:opacity-40"
            style={{
              color: 'var(--text-3)',
              transitionTimingFunction: 'var(--ease-flora)',
              transitionDuration: 'var(--motion-fast)',
            }}
            aria-label={`Clone ${game.displayName} to edit locally`}
          >
            {cloning ? (
              <div className="h-4 w-4 animate-spin rounded-full border border-white/20 border-t-white/60" />
            ) : (
              <Download size={16} strokeWidth={1.75} />
            )}
          </button>
        ) : null}
      </div>
      {/* Divider — inset by rounded-xl radius (12px) so it stops at the corner.
          Sits behind the hover surface (z-0) so hover bg covers it. */}
      {!isLast && (
        <div
          className="absolute bottom-0 left-3 right-3 h-px transition-opacity group-hover:opacity-0"
          style={{ background: 'var(--border-alpha-1)', transitionDuration: 'var(--motion-fast)' }}
        />
      )}
    </div>
  );
}

function CommunityGameCard({
  game,
  favorite = false,
  onFavorite,
  onArchive,
}: {
  game: DiscoveredGame;
  favorite?: boolean;
  onFavorite?: (favorite: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const canCloneGame = canInvokeChannel(capabilities, 'game:clone');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [imgFailed, setImgFailed] = useState(false);

  // Reset failure state when the image URL changes (e.g. after re-fetch)
  useEffect(() => {
    setImgFailed(false);
  }, [game.heroImage]);

  const handleClone = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (cloning || !canCloneGame) return;
    setCloning(true);
    setCloneError(null);
    try {
      await window.crucible.invoke('game:clone', {
        gameId: game.gameId,
        repoFullName: game.repoFullName,
      });
      invalidateGameLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['game:discover'] });
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Clone failed');
      setTimeout(() => setCloneError(null), 5000);
    } finally {
      setCloning(false);
    }
  };

  const handleFavorite = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    void onFavorite?.(!favorite);
  };

  const handleArchive = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (
      confirm(
        `Archive "${game.displayName}"? It will be hidden from the Dashboard and Community Games until you restore it.`,
      )
    ) {
      void onArchive?.();
    }
  };

  return (
    <div
      className="card-gradient group flex flex-col overflow-hidden rounded-2xl transition-all hover:brightness-[1.08]"
      style={{
        boxShadow: 'var(--hairline), var(--shadow-card)',
        transitionTimingFunction: 'var(--ease-flora)',
        transitionDuration: 'var(--motion-fast)',
      }}
    >
      {/* Hero image / placeholder.
          #603: swap the previous letter-avatar fallback for the same
          <GenreFallback> the dashboard's main GameCard uses, so cloned
          games and community games share a single visual fallback
          system. Genre is inferred from displayName + description
          (the GitHub repo description threaded through the discover
          flow), matching the keyword set the Fal.ai prompt builder
          would have used. */}
      <div className="relative flex aspect-video w-full items-center justify-center bg-gradient-to-br from-crucible-accent/20 to-purple-500/20">
        {game.heroImage && !imgFailed ? (
          <img
            src={game.heroImage}
            alt={game.displayName}
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <GenreFallback
            genreKey={inferGenreKey(game.displayName, game.description)}
            alt={game.displayName}
            className="h-full w-full object-cover"
          />
        )}
        {cloning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
            <span className="text-xs font-medium text-white/70">Cloning...</span>
          </div>
        )}
        {cloneError && (
          <div className="absolute inset-x-0 bottom-0 bg-red-900/90 px-3 py-2 text-xs text-red-200 backdrop-blur-sm">
            {cloneError}
          </div>
        )}
        {onFavorite && (
          <button
            type="button"
            onClick={handleFavorite}
            className={`absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 ${favorite ? 'text-[#FFDA0A]' : 'text-white/55'} opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-[#FFDA0A] group-hover:opacity-100`}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={
              favorite ? `Unfavorite ${game.displayName}` : `Favorite ${game.displayName}`
            }
          >
            <Star size={14} strokeWidth={1.8} fill={favorite ? 'currentColor' : 'none'} />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openExternalUrl(game.repoUrl);
          }}
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/55 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
          title="Open GitHub repo"
          aria-label={`Open GitHub repo for ${game.displayName}`}
        >
          <ExternalLink size={14} strokeWidth={1.8} />
        </button>
        {onArchive && (
          <button
            type="button"
            onClick={handleArchive}
            className="absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/55 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100"
            title="Archive game"
            aria-label={`Archive ${game.displayName}`}
          >
            <Archive size={14} strokeWidth={1.8} />
          </button>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-1 flex-col gap-1 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium group-hover:text-white" style={{ color: 'var(--text-1)' }}>
            {game.displayName}
          </h3>
          {game.isLocal ? (
            <span className="shrink-0 rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-medium text-green-400">
              Local copy
            </span>
          ) : canCloneGame ? (
            <button
              onClick={handleClone}
              disabled={cloning}
              className="shrink-0 rounded-full bg-crucible-accent/20 px-2 py-0.5 text-xs font-medium text-crucible-accent hover:bg-crucible-accent/30 disabled:opacity-40"
            >
              {cloning ? 'Cloning...' : 'Clone to edit'}
            </button>
          ) : null}
        </div>
        {game.description && (
          <p className="line-clamp-2 text-xs text-white/40">{game.description}</p>
        )}
        {!game.isLocal && canCloneGame && (
          <p className="text-xs text-white/30">Creates a local editable copy on this Mac.</p>
        )}
        <p className="text-xs text-white/30">{formatTimeAgo(game.updatedAt)}</p>
      </div>
    </div>
  );
}
